/**
 * Mitarbeiterakte — 4-Tab-Page mit allen Sektionen + Sub-Tabs.
 * Welle 2026-06-14 (Joerg, Vollausbau nach Mockups).
 *
 * Tabs: Stammdaten / Zugriffe / Sicherheit / Akte
 *   - Tab ohne Read → nicht gerendert
 *   - Sektion (Stammdaten) ohne Read → Karte „Keine Rechte"
 *   - Globaler Bearbeiten-Button im Sticky-Header schaltet alle Sektionen
 *     mit Write-Recht in den Edit-Modus
 */
import { useEffect, useMemo, useState, useCallback, useRef, Fragment, lazy, Suspense } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Lock, LockOpen, LogOut, KeyRound, ShieldOff, RefreshCcw, Plus, Loader2 } from 'lucide-react';
import { api, useAuth, authFetch } from '@onvice/portal-shell-api';

import AkteStrukturViewer from '../../components/AkteStrukturViewer';
import ModulePermissionsMatrix from '../../components/ModulePermissionsMatrix';
import { AuditActionCell } from '../../lib/auditText';
import { BackLink, EditBar, SaveFeedback, UnsavedChangesModal, colorForPerson, useBack, useDialogs, useEditSession, usePageLabel, useSaveParticipant, useSaveRegistry, type SaveRegistry } from '@onvice/portal-ui';

// Zeitkonto-Detail als Federation-Import — ersetzt seit 2026-08-08 die alte
// „Letzte 20 Stempel"-Liste durch die Monats-Detail-Ansicht mit Monatswechsler
// (identisch zur Standalone-Route /m/zeiterfassung/team-monat/:userId).
const UserMonthDetail = lazy(() => import('zeiterfassung/components/UserMonthDetail'));

/**
 * Joerg 2026-07-24: Kelly-22-Palette — dieselbe wie im Backend
 * (engine/apps/portal-backend/src/db/person-color-palette.ts). Bewusst
 * hier dupliziert, weil der Client-Bundle sonst die Backend-Datei
 * ueber ein Paket referenzieren muesste. Bei Aenderung beide Stellen
 * gleichzeitig pflegen.
 */
const KELLY_PALETTE: string[] = [
  '#FFB300', '#803E75', '#FF6800', '#A6BDD7', '#C10020',
  '#CEA262', '#817066', '#007D34', '#F6768E', '#00538A',
  '#FF7A5C', '#53377A', '#FF8E00', '#B32851', '#F4C800',
  '#7F180D', '#93AA00', '#593315', '#F13A13', '#232C16',
];

type FieldAccessLevel = 'none' | 'read' | 'write';
type FieldAccess = Record<string, FieldAccessLevel>;

interface PersonDetail {
  id: string;
  user_id: string;
  salutation: 'herr' | 'frau' | 'divers' | null;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  mobile?: string | null;
  birthday?: string | null;
  street?: string | null;
  street_extra?: string | null;
  zip?: string | null;
  city?: string | null;
  country_code?: string | null;
  emergency_name?: string | null;
  emergency_relation?: string | null;
  emergency_phone?: string | null;
  emergency_email?: string | null;
  /** Joerg 2026-07-24: Persistente Person-Farbe (Kelly-22 Palette).
   *  Wird auf der TagesMapView + im Mannschaft-Widget als Punkt-/Pin-Farbe genutzt. */
  color?: string | null;
  employed_since?: string | null;
  active?: boolean;
  user_role?: string;
  weekly_hours?: number;
  hourly_rate?: number | null;
  // Cluster 15.6 Sub-A (Probst 2026-06-25): Minijobber-Marker + Monatsdeckel.
  is_minijobber?: boolean;
  monthly_hours_cap?: number | null;
  // SD-2026-00003 (Martin 2026-07-28): Plantafel-Sichtbarkeit.
  show_in_plantafel?: boolean;
  // Beschäftigung / employee_settings
  hire_date?: string | null;
  exit_date?: string | null;
  // Zeitkonto-Uebernahme aus Altsystem (Joerg 2026-08-09):
  // positiv = Guthaben, negativ = Rueckstand. Wird zum akkumulierten
  // daily_balances-Saldo addiert (ab hire_date).
  start_saldo_minutes?: number | null;
  // Arbeitszeit / employee_settings
  weekly_target_minutes?: number | null;
  daily_target_minutes?: number[] | null;
  gps_policy?: 'off' | 'optin' | 'required' | null;
  // Loxone NFC (2026-09-14): Mitarbeiter-Stammfeld addressbook.persons.nfc_tag_uid
  nfc_tag_uid?: string | null;
  // Personalnummer (Martin 2026-09-15): alternatives Terminal-Merkmal.
  personalnummer?: string | null;
  // Konditionen / leave_quotas
  leave_quotas?: { year: number; type: string; total_days: number; carryover_days: number }[];
  // Zugriffe-Tab
  groups?: { id: string; name: string }[];
  access?: { module_slug: string; effective_level: string; sources: { type: string; name: string; level: string }[] }[];
  doc_shares?: { module_slug: string; doc_id: string; level: string; granted_at: string }[];
  // Sicherheit-Tab
  totp_enabled?: boolean;
  totp_enabled_at?: string | null;
  last_login_at?: string | null;
  failed_attempts?: number;
  locked?: boolean;
  last_failed_at?: string | null;
  sessions?: { token_id: string; expires_at: string; created_at: string; user_agent: string | null; ip: string | null }[];
}

interface AuditEntry {
  id: string; actor_full_name: string; action: string;
  details: Record<string, unknown>; created_at: string;
}

interface AntragRow {
  id: string; type: string; from_date: string; to_date: string;
  half_day_from: boolean; half_day_to: boolean; status: string;
  /** ABW 2026-08-28: NULL = ganztaegig; sonst Stunden-Zeitausgleich in Minuten. */
  duration_minutes?: number | null;
  aufschluesselung: { brutto: number; feiertage: number; krank: number; netto: number };
  approver_first_name?: string | null; approver_last_name?: string | null;
  approved_at?: string | null;
  /** Joerg 2026-07-04: Anzahl Tage im Antrags-Zeitraum, an denen der MA
   *  tatsaechlich gestempelt hat. Nur relevant fuer type='urlaub' — dann
   *  entscheidet GF ob Saldo angepasst wird. Portal macht nichts automatisch. */
  overlap_stamps_days?: number;
  overlap_stamps_minutes?: number;
}

interface SaldoEntry {
  id: string; day: string; minutes: number; kind: string; reason: string | null;
  source_id: string | null; created_at: string;
}

interface AddressbookGroup { id: string; name: string }

const TAB_DEF = [
  { key: 'stammdaten', slug: 'addressbook.tab.stammdaten', label: 'Stammdaten' },
  { key: 'zugriffe',   slug: 'addressbook.tab.zugriffe',   label: 'Zugriffe' },
  { key: 'sicherheit', slug: 'addressbook.tab.sicherheit', label: 'Sicherheit' },
  { key: 'akte',       slug: 'addressbook.tab.akte',       label: 'Akte' },
] as const;
type TabKey = (typeof TAB_DEF)[number]['key'];
type SubTabKey = 'antraege' | 'zeiterfassung' | 'arbeit' | 'aktestruktur' | 'dokumente' | 'zertifikate' | 'controlling' | 'verlauf';

const TYPE_LABEL: Record<string, string> = {
  urlaub: 'Urlaub', krank: 'Krank', sonderurlaub: 'Sonderurlaub',
  unbezahlt: 'Unbezahlt', schule: 'Berufsschule', sonstiges: 'Sonstiges',
  zeitausgleich: 'Zeitausgleich',
};
/** Feinschliff 2026-10-28: Saldo-Bewegungen lesbar statt „zeitausgleich · -120“. */
const SALDO_KIND_LABEL: Record<string, string> = {
  zeitausgleich: 'Zeitausgleich', auszahlung: 'Auszahlung', manuell: 'Manuelle Buchung', urlaubsrueckgabe: 'Urlaubsrückgabe',
};
const SALUTATION_LABEL: Record<string, string> = { herr: 'Herr', frau: 'Frau', divers: 'Divers' };
const STATUS_CLASS: Record<string, string> = {
  beantragt: 'bg-amber-500/15 text-amber-300',
  genehmigt: 'bg-brand-500/15 text-brand-300',
  abgelehnt: 'bg-rose-500/15 text-rose-300',
  storniert: 'bg-slate-500/15 text-slate-300',
};
const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrator', gf: 'Geschäftsführung', buero: 'Büro',
  vorarbeiter: 'Vorarbeiter', handwerker: 'Handwerker', softwaretester: 'Softwaretester',
};
const ALL_ROLES = ['handwerker', 'vorarbeiter', 'buero', 'gf', 'admin', 'softwaretester'];
const LEVEL_BG: Record<string, string> = {
  admin:    'bg-brand-500/15 text-brand-300',
  editor:   'bg-sky-500/15 text-sky-300',
  author:   'bg-blue-500/15 text-blue-300',
  tester:   'bg-purple-500/15 text-purple-300',
  reader:   'bg-slate-500/15 text-slate-300',
  depositor:'bg-amber-500/15 text-amber-300',
  none:     'bg-slate-700/40 text-slate-500',
};

function fmtDate(iso?: string | null): string {
  if (!iso) return '–';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00Z' : ''));
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmtDateTime(iso?: string | null): string {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtTage(n: number): string {
  if (n === 0) return '0';
  return Number.isInteger(n) ? `${n}` : n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
/** ABW 2026-08-28: Minuten eines Stunden-Zeitausgleichs als Stunden ("1,5"). */
function fmtStd(minutes: number): string {
  return (minutes / 60).toLocaleString('de-DE', { maximumFractionDigits: 2 });
}
function fmtMinutes(min: number | null | undefined): string {
  if (min == null) return '–';
  const sign = min < 0 ? '−' : '';
  const a = Math.abs(min);
  const h = Math.floor(a / 60), m = a % 60;
  return `${sign}${h} h ${m.toString().padStart(2, '0')} min`;
}

export default function MitarbeiterAktePage() {
  // Zwei Einstiegspfade:
  //   /admin/adressbuch/persons/by-user/:userId → userId-Param
  //   /admin/adressbuch/persons/:id             → id-Param (= personId)
  const params = useParams<{ userId?: string; id?: string }>();
  const userIdParam = params.userId;
  const personIdParam = params.id;
  const [personId, setPersonId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [person, setPerson] = useState<PersonDetail | null>(null);
  const [access, setAccess] = useState<FieldAccess>({});
  // Tab + Sub-Tab in URL-Query persistieren, damit Browser-Back aus Detail-
  // Pages (Bericht, Aufgabe etc.) wieder im richtigen Sub-Tab landet.
  // Dokument-Navigation 1.42 (Jörg 2026-09-29): ersetzend geschrieben — Reiterwechsel
  // erzeugen keinen Verlaufseintrag; `?edit=1` ist nur Tiefenlink.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const tab: TabKey = TAB_DEF.some((t) => t.key === urlTab) ? (urlTab as TabKey) : 'stammdaten';
  const setTab = (k: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', k);
    if (k !== 'akte') next.delete('sub');
    setSearchParams(next, { replace: true, preventScrollReset: true });
  };
  const dlg = useDialogs();
  const back = useBack({ fallback: '/admin/adressbuch', fallbackLabel: 'Adressbuch' });
  const initialEdit = useRef(searchParams.get('edit') === '1' || searchParams.get('edit') === 'true').current;
  useEffect(() => {
    if (!searchParams.has('edit')) return;
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('edit'); return next; }, { replace: true, preventScrollReset: true });
  }, [searchParams, setSearchParams]);
  // BA (Joerg 2026-08-28): Der allgemeine Speichern-Knopf sichert AUCH offene
  // Aenderungen der Arbeitszeitmodell-Karte. 1.42: verallgemeinert als Sammelstelle —
  // Arbeitszeitmodell, Gruppen-Haken und Modul-Rechte melden sich dort an; nichts
  // davon schreibt mehr sofort (ersetzt das Sofort-Speichern der Modulrechte vom 19./20.05.).
  const { registry, anyDirty: partsDirty } = useSaveRegistry();
  const [antraege, setAntraege] = useState<AntragRow[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const visibleTabs = useMemo(
    () => TAB_DEF.filter((t) => (access[t.slug] ?? 'none') !== 'none'),
    [access],
  );
  const hasAnyWrite = useMemo(() => Object.values(access).some((l) => l === 'write'), [access]);

  const reloadDetail = useCallback(async (pid: string) => {
    const detail = await api.get<{ success: boolean; data: PersonDetail; fieldAccess: FieldAccess }>(
      `/admin/adressbuch/persons/${pid}`,
    );
    setPerson(detail.data);
    setAccess(detail.fieldAccess ?? {});
    return detail;
  }, []);

  // Lese-/Bearbeitungsmodus mit Arbeitskopie (R3–R5): „Speichern" → Lesemodus, Akte bleibt
  // offen (E1); „Abbrechen" → dorthin, wo der Anwender herkam, mit Rückfrage (E2).
  const session = useEditSession<PersonDetail>({
    id: `person:${personIdParam ?? userIdParam ?? ''}`,
    label: 'Mitarbeiterakte',
    baseline: person,
    initialMode: initialEdit ? 'edit' : 'read',
    canEdit: hasAnyWrite,
    extraDirty: partsDirty,
    onSave: (value) => savePerson(value),
    onDiscard: registry.discardAll,
    onLeave: back.go,
  });
  const editing = session.editing;
  const saving = session.saving;
  const form: Partial<PersonDetail> = session.value ?? {};
  usePageLabel(person ? `${person.first_name} ${person.last_name}` : null, 'document');

  useEffect(() => {
    if (!userIdParam && !personIdParam) return;
    setLoading(true); setError(null);
    (async () => {
      try {
        let pid: string;
        if (personIdParam) {
          pid = personIdParam;
        } else {
          const lookup = await api.get<{ success: boolean; data: { person_id: string } }>(
            `/admin/adressbuch/by-user/${userIdParam}`,
          );
          pid = lookup.data.person_id;
        }
        setPersonId(pid);
        const detail = await reloadDetail(pid);
        const uid = detail.data.user_id;
        setUserId(uid);
        // Absences erst nachladen, sobald userId bekannt ist
        try {
          const ant = await api.get<{ success: boolean; data: AntragRow[] }>(
            `/modules/zeiterfassung/admin/absences/by-user/${uid}`,
          );
          setAntraege(ant.data ?? []);
        } catch { setAntraege([]); }
        // Wenn kein tab in URL UND kein Stammdaten-Zugriff → ersten sichtbaren Tab setzen.
        if (!searchParams.get('tab')) {
          const first = TAB_DEF.find((t) => (detail.fieldAccess?.[t.slug] ?? 'none') !== 'none');
          if (first && first.key !== 'stammdaten') setTab(first.key);
        }
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [userIdParam, personIdParam, reloadDetail]);

  useEffect(() => {
    if (tab !== 'sicherheit' || !personId) return;
    if ((access['addressbook.tab.sicherheit'] ?? 'none') === 'none') return;
    api.get<{ success: boolean; data: AuditEntry[] }>(`/admin/adressbuch/persons/${personId}/audit`)
      .then((r) => setAudit(r.data ?? []))
      .catch(() => setAudit([]));
  }, [tab, personId, access]);

  if (!userIdParam && !personIdParam) return <div className="p-6 text-slate-400">Kein Mitarbeiter ausgewählt.</div>;
  if (loading) return <div className="p-6 text-slate-400">Lädt…</div>;
  if (error)   return <div className="p-6 text-rose-400">Fehler: {error}</div>;
  if (!person) return <div className="p-6 text-slate-400">Person nicht gefunden.</div>;

  const fullName = `${person.first_name} ${person.last_name}`;

  /** Schreibt die Arbeitskopie; wirft bei Fehlern (Meldung über die Sitzung). */
  async function savePerson(form: PersonDetail): Promise<void> {
    if (!personId || !person) return;
    setMsg(null);
    {
      const body: Record<string, unknown> = {};
      const F = form as unknown as Record<string, unknown>;
      const P = person as unknown as Record<string, unknown>;
      const fields = [
        'salutation', 'firstName', 'lastName', 'email', 'birthday', 'phone', 'mobile',
        'street', 'streetExtra', 'zip', 'city', 'countryCode',
        'emergencyName', 'emergencyRelation', 'emergencyPhone', 'emergencyEmail',
        'active', 'role', 'employedSince', 'exitDate',
        'weeklyHours', 'weeklyTargetMinutes', 'dailyTargetMinutes', 'gpsPolicy',
        'nfcTagUid',
        'personalnummer',
        'hourlyRate',
        // Cluster 15.6 Sub-A
        'isMinijobber', 'monthlyHoursCap',
        // SD-2026-00003 (Martin 2026-07-28): Plantafel-Sichtbarkeit.
        'showInPlantafel',
        // Jörg 2026-07-24 UC: Farbe (persistente Pin-Farbe). Fehlender
        // Eintrag hier hat verhindert, dass das color-Feld ueberhaupt in
        // den PATCH-Body kam — Backend hatte FIELD_MAP + Spalte bereit.
        'color',
      ];
      const camelToSnake = (s: string): string => s.replace(/[A-Z]/g, (m) => '_' + m.toLowerCase());
      for (const key of fields) {
        const snake = camelToSnake(key);
        const v = F[snake] !== undefined ? F[snake] : F[key];
        const orig = P[snake] !== undefined ? P[snake] : P[key];
        if (v !== undefined && JSON.stringify(v) !== JSON.stringify(orig)) body[key] = v;
      }
      // 2026-07-17: Leave-Quotas-Diff — geht nicht an /admin/adressbuch/persons,
      // sondern separat an /api/modules/zeiterfassung/admin/leave-quotas/:userId
      // (pro geaendertem/neuem Jahr ein PUT). Personen-user_id ist seit Welle 6
      // identisch mit personId (Werte-Swap), aber semantisch korrekt: user_id.
      type Q = { year: number; type: string; total_days: number; carryover_days: number };
      const formQuotas: Q[] = form.leave_quotas ?? [];
      const origQuotas: Q[] = person.leave_quotas ?? [];
      const quotaChanges: Q[] = [];
      for (const q of formQuotas) {
        if (q.type !== 'urlaub') continue;
        const orig = origQuotas.find((o) => o.year === q.year && o.type === 'urlaub');
        if (!orig || Number(orig.total_days) !== Number(q.total_days) || Number(orig.carryover_days) !== Number(q.carryover_days)) {
          quotaChanges.push(q);
        }
      }

      // Joerg 2026-08-09: Zeitkonto-Config (hire_date/exit_date/start_saldo_minutes)
      // geht separat an /api/modules/zeiterfassung/admin/employee-settings/:userId.
      // Der Endpoint triggert automatisch recomputeRange, wenn hire/exit/target
      // sich aendern — damit ist der daily_balances-Cache sofort konsistent, ohne
      // dass Buero „Neu berechnen" klicken muss.
      const hireDateOrig = person.hire_date ?? null;
      const hireDateForm = (form.hire_date ?? null) as string | null;
      const exitDateOrig = person.exit_date ?? null;
      const exitDateForm = (form.exit_date ?? null) as string | null;
      const startSaldoOrig = person.start_saldo_minutes ?? 0;
      const startSaldoForm = form.start_saldo_minutes ?? 0;
      const settingsChanged =
        hireDateForm !== hireDateOrig ||
        exitDateForm !== exitDateOrig ||
        Number(startSaldoForm) !== Number(startSaldoOrig);

      // BA: offene Aenderungen der angemeldeten Teile (Arbeitszeitmodell, Gruppen,
      // Modul-Rechte) mitsichern — Joerg-Erwartung: EIN Speichern-Knopf sichert alles.
      // Ein Teilfehler hält die übrigen nicht auf; er wird gesammelt gemeldet.
      const parts = await registry.flushAll();
      if (Object.keys(body).length > 0) {
        await api.put(`/admin/adressbuch/persons/${personId}`, body);
      }
      for (const q of quotaChanges) {
        const r = await fetch(`/api/modules/zeiterfassung/admin/leave-quotas/${person.user_id}`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ year: q.year, totalDays: Number(q.total_days), carryoverDays: Number(q.carryover_days) }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
          throw new Error(`Urlaub ${q.year}: ${(err as { error?: string }).error ?? r.statusText}`);
        }
      }
      if (settingsChanged) {
        const r = await fetch(`/api/modules/zeiterfassung/admin/employee-settings/${person.user_id}`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            hireDate: hireDateForm,
            exitDate: exitDateForm,
            startSaldoMinutes: Number(startSaldoForm),
          }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
          throw new Error(`Zeitkonto-Konfig: ${(err as { error?: string }).error ?? r.statusText}`);
        }
      }
      await reloadDetail(personId);
      if (!parts.ok) {
        throw new Error(`Nicht gespeichert: ${parts.failed.join(', ')} — die übrigen Angaben sind gesichert.`);
      }
      setMsg('Gespeichert.');
    }
  }

  const setField = (k: keyof PersonDetail, v: unknown) => session.setField(k, v as never);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-4" data-test-anchor="person-page" data-mode={session.mode}>
      <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 border-b border-slate-800">
        <div className="flex items-center gap-3 flex-wrap">
          <BackLink fallback="/admin/adressbuch" fallbackLabel="Adressbuch" />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100">{fullName}</h1>
          {person.active === false && (
            <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300">inaktiv</span>
          )}
          {person.locked && (
            <span className="text-xs px-2 py-0.5 rounded bg-rose-500/15 text-rose-300">gesperrt</span>
          )}
          {session.dirty && <span data-state="dirty" className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40">ungespeichert</span>}
          <div className="flex-1" />
          <EditBar
            editing={editing}
            dirty={session.dirty}
            saving={saving}
            canEdit={hasAnyWrite}
            onEdit={() => { setMsg(null); session.startEdit(); }}
            onSave={() => { void session.save(); }}
            onCancel={session.cancel}
          />
        </div>
        {visibleTabs.length > 0 && (
          <nav className="flex mt-3 -mb-3 border-b-0">
            {visibleTabs.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                        tab === t.key
                          ? 'border-brand-500 text-brand-300'
                          : 'border-transparent text-slate-400 hover:text-slate-200'
                      }`}>
                {t.label}
                {t.key === 'akte' && antraege.length > 0 && ` (${antraege.length})`}
              </button>
            ))}
          </nav>
        )}
      </div>

      {msg && (
        <div className={`px-3 py-2 rounded-lg text-sm ${msg.startsWith('Fehler') ? 'bg-rose-500/10 text-rose-300' : 'bg-brand-500/10 text-brand-300'}`}>
          {msg}
        </div>
      )}

      {tab === 'stammdaten' && (
        <StammdatenTab person={person} form={form} setField={setField} editing={editing} access={access} />
      )}
      {tab === 'zugriffe' && personId && (
        <ZugriffeTab person={person} form={form} setField={setField} editing={editing} access={access}
                     personId={personId} registry={registry}
                     onAction={() => reloadDetail(personId).catch((e: unknown) => { void dlg.error('Neu laden fehlgeschlagen', e); })} />
      )}
      {tab === 'sicherheit' && personId && (
        <SicherheitTab person={person} access={access} audit={audit} personId={personId}
                       onAction={() => {
                         reloadDetail(personId).catch((e: unknown) => { void dlg.error('Neu laden fehlgeschlagen', e); });
                         api.get<{ success: boolean; data: AuditEntry[] }>(
                           `/admin/adressbuch/persons/${personId}/audit`,
                         ).then((r) => setAudit(r.data ?? [])).catch(() => setAudit([]));
                         setMsg('Aktion ausgeführt.');
                       }} />
      )}
      {tab === 'akte' && (
        <AkteTab antraege={antraege} userId={userId ?? ''} personId={personId} audit={audit}
                 canWriteAkte={(access['addressbook.tab.akte'] ?? 'none') === 'write'}
                 editing={editing} form={form} setField={setField} person={person}
                 registry={registry} />
      )}
      <UnsavedChangesModal {...session.unsavedModalProps} />
      <SaveFeedback label="Mitarbeiterakte" error={session.error} clearError={session.clearError} issues={session.issues} clearIssues={session.clearIssues} />
    </div>
  );
}

// =========================== TAB: Stammdaten ===========================

function Section({ title, slug, access, editing, write, children }:
  { title: string; slug: string; access: FieldAccess; editing: boolean; write?: boolean; children: React.ReactNode }) {
  const level = access[slug] ?? 'none';
  if (level === 'none') {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/30 p-4">
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">{title}</h3>
          <span className="text-xs text-slate-500">Keine Rechte für diesen Abschnitt</span>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">{title}</h3>
        {editing && !write && <span className="text-xs text-slate-500">Nur Leserecht</span>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
const inp = 'w-full px-3 py-2 text-sm rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:border-brand-500 focus:outline-none disabled:opacity-60';
function ReadOnly({ value }: { value: string | number | null | undefined }) {
  return <div className="text-sm text-slate-100">{value == null || value === '' ? '–' : String(value)}</div>;
}

function StammdatenTab({ person, form, setField, editing, access }: {
  person: PersonDetail; form: Partial<PersonDetail>;
  setField: (k: keyof PersonDetail, v: unknown) => void;
  editing: boolean; access: FieldAccess;
}) {
  const w = (slug: string): boolean => (access[slug] ?? 'none') === 'write';
  const can = (slug: string): boolean => editing && w(slug);
  // 1.29.0 Pausenregelung (Jörg 2026-10-20: „im Stammdatenabschnitt nur eine Anzeige des aktuell
  // gültigen Modells, keine Standardvorgabe“): Wochenstunden + heute gültige Pausen aus dem Modell.
  const [emp, setEmp] = useState<MzEmploymentData | null>(null);
  useEffect(() => {
    if (!person.user_id) { setEmp(null); return; }
    let alive = true;
    (mzApi('GET', `/admin/employment/${person.user_id}`) as Promise<MzEmploymentData>)
      .then((d) => { if (alive) setEmp(d); })
      .catch(() => { if (alive) setEmp(null); });
    return () => { alive = false; };
  }, [person.user_id]);
  const modelWeekly = emp?.daily_target_minutes && emp.daily_target_minutes.length > 0
    ? emp.daily_target_minutes.reduce((a, b) => a + (Number(b) || 0), 0) / 60
    : null;
  const weeklyText = modelWeekly != null
    ? `${Number.isInteger(modelWeekly) ? String(modelWeekly) : modelWeekly.toLocaleString('de-DE', { maximumFractionDigits: 1 })} h`
    : (person.weekly_hours != null ? `${person.weekly_hours} h` : '–');

  // AZM-H8 (2026-08-28): Tages-Soll ist hier nur noch Anzeige — die Pflege
  // laeuft historisiert (Perioden mit "Gilt ab") ueber die Arbeitszeitmodell-
  // Karte im Zeiterfassung-Sub-Tab. Der alte Schreibweg lief am Recompute
  // vorbei und schrieb 5er-Arrays (Backend ignoriert das Feld inzwischen).

  // Leave-Quotas pro Jahr. 2026-07-17: im Edit-Modus editierbar (vorher komplett
  // read-only mit Verweis „Pflege im Zeiterfassung-Modul", das aber seit dem
  // 2026-06-14-Umbau nirgends mehr existierte → Feature-Luecke).
  // Quelle: im Edit-Modus form.leave_quotas (User-Aenderungen), sonst
  // person.leave_quotas (Server-Stand).
  const quotas = (editing ? form.leave_quotas : person.leave_quotas) ?? [];
  const quotasByYear = useMemo(() => {
    const map = new Map<number, { total: number; carry: number }>();
    for (const q of quotas) {
      if (q.type === 'urlaub') {
        map.set(q.year, { total: Number(q.total_days), carry: Number(q.carryover_days) });
      }
    }
    return Array.from(map.entries()).sort(([a], [b]) => b - a);
  }, [quotas]);
  const canEditQuotas = can('addressbook.section.konditionen');
  const setQuota = (year: number, patch: { total?: number; carry?: number }): void => {
    const list = (form.leave_quotas ?? person.leave_quotas ?? []).map((q) => ({ ...q }));
    const idx = list.findIndex((q) => q.year === year && q.type === 'urlaub');
    if (idx >= 0) {
      if (patch.total !== undefined) list[idx]!.total_days = patch.total;
      if (patch.carry !== undefined) list[idx]!.carryover_days = patch.carry;
    } else {
      list.push({ year, type: 'urlaub', total_days: patch.total ?? 0, carryover_days: patch.carry ?? 0 });
    }
    setField('leave_quotas', list);
  };
  const addYear = (): void => {
    const list = form.leave_quotas ?? person.leave_quotas ?? [];
    const usedYears = new Set(list.filter((q) => q.type === 'urlaub').map((q) => q.year));
    // Aktuelles Jahr, sonst nächstes freies Jahr rückwärts (2026 → 2025 → …).
    const now = new Date().getFullYear();
    let year = now;
    while (usedYears.has(year) && year > now - 20) year--;
    setQuota(year, { total: 30, carry: 0 });
  };

  return (
    <div className="space-y-4">
      <Section title="Personalien" slug="addressbook.section.personalien" access={access} editing={editing} write={w('addressbook.section.personalien')}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Anrede">
            {can('addressbook.section.personalien') ? (
              <select className={inp} value={form.salutation ?? ''} onChange={(e) => setField('salutation', e.target.value || null)}>
                <option value="">–</option><option value="herr">Herr</option><option value="frau">Frau</option><option value="divers">Divers</option>
              </select>
            ) : <ReadOnly value={person.salutation ? SALUTATION_LABEL[person.salutation] : null} />}
          </Field>
          <Field label="Vorname">
            {can('addressbook.section.personalien')
              ? <input className={inp} value={form.first_name ?? ''} onChange={(e) => setField('first_name', e.target.value)} />
              : <ReadOnly value={person.first_name} />}
          </Field>
          <Field label="Nachname">
            {can('addressbook.section.personalien')
              ? <input className={inp} value={form.last_name ?? ''} onChange={(e) => setField('last_name', e.target.value)} />
              : <ReadOnly value={person.last_name} />}
          </Field>
          <Field label="Geburtstag">
            {can('addressbook.section.personalien')
              ? <input type="date" className={inp} value={(form.birthday ?? '').slice(0, 10)} onChange={(e) => setField('birthday', e.target.value || null)} />
              : <ReadOnly value={fmtDate(person.birthday)} />}
          </Field>
          <Field label="E-Mail">
            {can('addressbook.section.personalien')
              ? <input type="email" className={inp} value={form.email ?? ''} onChange={(e) => setField('email', e.target.value)} />
              : <ReadOnly value={person.email} />}
          </Field>
          <Field label="Telefon">
            {can('addressbook.section.personalien')
              ? <input className={inp} value={form.phone ?? ''} onChange={(e) => setField('phone', e.target.value || null)} />
              : <ReadOnly value={person.phone} />}
          </Field>
          <Field label="Mobil">
            {can('addressbook.section.personalien')
              ? <input className={inp} value={form.mobile ?? ''} onChange={(e) => setField('mobile', e.target.value || null)} />
              : <ReadOnly value={person.mobile} />}
          </Field>
          <Field label="Farbe (Karten-Pin, Mannschaft-Punkt)">
            {can('addressbook.section.personalien') ? (
              <div className="flex flex-wrap items-center gap-1.5">
                {/* Aktueller Wert (oder Hash-Fallback) als grosse Vorschau */}
                <span
                  className="inline-block w-6 h-6 rounded-full border border-slate-300 shadow-sm"
                  style={{ backgroundColor: colorForPerson({ id: person.id, color: form.color ?? null }) }}
                  title={form.color ?? '(Hash-Fallback)'}
                />
                {KELLY_PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setField('color', c)}
                    className={`w-5 h-5 rounded-full border ${(form.color ?? '').toLowerCase() === c.toLowerCase() ? 'border-slate-900 ring-2 ring-slate-900/40' : 'border-slate-300'}`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
                <input
                  type="color"
                  className="w-8 h-6 border border-slate-300 rounded cursor-pointer bg-transparent"
                  value={form.color ?? '#808080'}
                  onChange={(e) => setField('color', e.target.value)}
                  title="Freie Farbe"
                />
                {form.color && (
                  <button
                    type="button"
                    onClick={() => setField('color', null)}
                    className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2 ml-1"
                    title="Zurueck auf Hash-Fallback"
                  >zuruecksetzen</button>
                )}
              </div>
            ) : (
              <span
                className="inline-block w-5 h-5 rounded-full border border-slate-300 align-middle"
                style={{ backgroundColor: colorForPerson({ id: person.id, color: person.color ?? null }) }}
                title={person.color ?? '(Hash-Fallback)'}
              />
            )}
          </Field>
        </div>
      </Section>

      <Section title="Anschrift" slug="addressbook.section.anschrift" access={access} editing={editing} write={w('addressbook.section.anschrift')}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Straße + Hausnr.">
            {can('addressbook.section.anschrift')
              ? <input className={inp} value={form.street ?? ''} onChange={(e) => setField('street', e.target.value || null)} />
              : <ReadOnly value={person.street} />}
          </Field>
          <Field label="Adresszusatz">
            {can('addressbook.section.anschrift')
              ? <input className={inp} value={form.street_extra ?? ''} onChange={(e) => setField('street_extra', e.target.value || null)} />
              : <ReadOnly value={person.street_extra} />}
          </Field>
          <div />
          <Field label="PLZ">
            {can('addressbook.section.anschrift')
              ? <input className={inp} value={form.zip ?? ''} onChange={(e) => setField('zip', e.target.value || null)} />
              : <ReadOnly value={person.zip} />}
          </Field>
          <Field label="Ort">
            {can('addressbook.section.anschrift')
              ? <input className={inp} value={form.city ?? ''} onChange={(e) => setField('city', e.target.value || null)} />
              : <ReadOnly value={person.city} />}
          </Field>
          <Field label="Land">
            {can('addressbook.section.anschrift') ? (
              <select className={inp} value={form.country_code ?? 'DE'} onChange={(e) => setField('country_code', e.target.value)}>
                <option value="DE">Deutschland</option><option value="AT">Österreich</option>
                <option value="CH">Schweiz</option><option value="NL">Niederlande</option><option value="PL">Polen</option>
              </select>
            ) : <ReadOnly value={person.country_code} />}
          </Field>
        </div>
      </Section>

      <Section title="Notfallkontakt" slug="addressbook.section.notfallkontakt" access={access} editing={editing} write={w('addressbook.section.notfallkontakt')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name">
            {can('addressbook.section.notfallkontakt')
              ? <input className={inp} value={form.emergency_name ?? ''} onChange={(e) => setField('emergency_name', e.target.value || null)} />
              : <ReadOnly value={person.emergency_name} />}
          </Field>
          <Field label="Beziehung">
            {can('addressbook.section.notfallkontakt')
              ? <input className={inp} placeholder="z.B. Ehepartnerin, Mutter" value={form.emergency_relation ?? ''} onChange={(e) => setField('emergency_relation', e.target.value || null)} />
              : <ReadOnly value={person.emergency_relation} />}
          </Field>
          <Field label="Telefon">
            {can('addressbook.section.notfallkontakt')
              ? <input className={inp} value={form.emergency_phone ?? ''} onChange={(e) => setField('emergency_phone', e.target.value || null)} />
              : <ReadOnly value={person.emergency_phone} />}
          </Field>
          <Field label="E-Mail">
            {can('addressbook.section.notfallkontakt')
              ? <input type="email" className={inp} value={form.emergency_email ?? ''} onChange={(e) => setField('emergency_email', e.target.value || null)} />
              : <ReadOnly value={person.emergency_email} />}
          </Field>
        </div>
      </Section>

      <Section title="Beschäftigung" slug="addressbook.section.beschaeftigung" access={access} editing={editing} write={w('addressbook.section.beschaeftigung')}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Rolle (Zugriffe-Tab)">
            <ReadOnly value={ROLE_LABEL[person.user_role ?? ''] ?? person.user_role} />
          </Field>
          <Field label="Eintritt">
            {can('addressbook.section.beschaeftigung')
              ? <input type="date" className={inp} value={(form.employed_since ?? '').slice(0, 10)} onChange={(e) => setField('employed_since', e.target.value || null)} />
              : <ReadOnly value={fmtDate(person.employed_since)} />}
          </Field>
          <Field label="Austritt (optional)">
            {can('addressbook.section.beschaeftigung')
              ? <input type="date" className={inp} value={(form.exit_date ?? '').slice(0, 10)} onChange={(e) => setField('exit_date', e.target.value || null)} />
              : <ReadOnly value={fmtDate(person.exit_date)} />}
          </Field>
          <Field label="Status">
            {can('addressbook.section.beschaeftigung') ? (
              <select className={inp} value={form.active === false ? '0' : '1'} onChange={(e) => setField('active', e.target.value === '1')}>
                <option value="1">aktiv</option><option value="0">inaktiv</option>
              </select>
            ) : <ReadOnly value={person.active === false ? 'inaktiv' : 'aktiv'} />}
          </Field>
          {/* Cluster 15.6 Sub-A (Probst 2026-06-25): Minijobber-Marker + Monats-Deckel. */}
          <Field label="Minijobber">
            {can('addressbook.section.beschaeftigung') ? (
              <select className={inp} value={form.is_minijobber ? '1' : '0'} onChange={(e) => setField('is_minijobber', e.target.value === '1')}>
                <option value="0">Nein</option><option value="1">Ja</option>
              </select>
            ) : <ReadOnly value={person.is_minijobber ? 'Ja' : 'Nein'} />}
          </Field>
          <Field label="Monats-Stunden-Deckel">
            {can('addressbook.section.beschaeftigung') ? (
              <input type="number" step="0.5" className={inp} value={form.monthly_hours_cap ?? ''}
                onChange={(e) => setField('monthly_hours_cap', e.target.value === '' ? null : Number(e.target.value))}
                placeholder="z.B. 40" />
            ) : <ReadOnly value={person.monthly_hours_cap != null ? `${person.monthly_hours_cap} h` : '–'} />}
          </Field>
          {/* SD-2026-00003 (Martin 2026-07-28): Steuert ob Mitarbeiter in der
              Plantafel-Personalliste erscheint. Default an — Buero/GF fuer die
              die Plantafel nicht relevant ist koennen ausklinkt werden. */}
          <Field label="Auf Plantafel anzeigen">
            {can('addressbook.section.beschaeftigung') ? (
              <select className={inp} value={form.show_in_plantafel === false ? '0' : '1'} onChange={(e) => setField('show_in_plantafel', e.target.value === '1')}>
                <option value="1">Ja</option><option value="0">Nein</option>
              </select>
            ) : <ReadOnly value={person.show_in_plantafel === false ? 'Nein' : 'Ja'} />}
          </Field>
        </div>
      </Section>

      <Section title="Arbeitszeit" slug="addressbook.section.arbeitszeit" access={access} editing={editing} write={w('addressbook.section.arbeitszeit')}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <Field label="Wochenstunden">
            {/* 1.29.0 (Jörg 2026-10-20): kein Eingabefeld mehr — Anzeige der Wochensumme aus dem
                heute gültigen Arbeitszeitmodell (Fallback: gespeicherter Wert ohne Modell). */}
            <ReadOnly value={weeklyText} />
            <div className="text-xs text-slate-500 mt-1">
              Aus dem Arbeitszeitmodell — Pflege im Reiter „Zeiterfassung".
            </div>
          </Field>
          <Field label="Feste Pausen (heute gültig)">
            <ReadOnly value={describeEffectiveBreak(emp?.effective_break_today)} />
            <div className="text-xs text-slate-500 mt-1">
              Pflege im Reiter „Zeiterfassung" unter „Arbeitszeitmodell" — Betriebsregel unter Zeiterfassung → Pausenregelung.
            </div>
          </Field>
          <Field label="GPS-Policy">
            {can('addressbook.section.arbeitszeit') ? (
              <select className={inp} value={form.gps_policy ?? 'optin'} onChange={(e) => setField('gps_policy', e.target.value)}>
                <option value="off">off — kein GPS</option>
                <option value="optin">optin — Vorschlag</option>
                <option value="required">required — Pflicht</option>
              </select>
            ) : <ReadOnly value={person.gps_policy} />}
          </Field>
          <Field label="NFC-Tag (Zeiterfassung)">
            {can('addressbook.section.arbeitszeit')
              ? <input className={inp} value={form.nfc_tag_uid ?? ''} placeholder="UID des Loxone-NFC-Tags"
                       onChange={(e) => setField('nfc_tag_uid', e.target.value.trim() || null)} />
              : <ReadOnly value={person.nfc_tag_uid ?? '–'} />}
            <div className="text-[11px] text-slate-500 mt-1">
              Ein Tap am Loxone Code Touch stempelt für diesen Mitarbeiter Kommen/Gehen.
            </div>
          </Field>
          <Field label="Personalnummer">
            {can('addressbook.section.arbeitszeit')
              ? <input className={inp} value={form.personalnummer ?? ''} placeholder="z. B. 4711"
                       onChange={(e) => setField('personalnummer', e.target.value.trim() || null)} />
              : <ReadOnly value={person.personalnummer ?? '–'} />}
            <div className="text-[11px] text-slate-500 mt-1">
              Eindeutig pro Mitarbeiter. Kann am Zeiterfassungs-Terminal statt des NFC-Tags übergeben werden.
            </div>
          </Field>
        </div>
        <div>
          {/* AZM-H8 (2026-08-28): nur noch Anzeige — das Tagessoll ist eine
              historisierte Perioden-Zeitachse; Pflege mit „Gilt ab" im
              Reiter Zeiterfassung unter „Arbeitszeitmodell". */}
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Tages-Soll (Minuten, Mo–So)</label>
          <div className="grid grid-cols-7 gap-2">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day, i) => (
              <div key={day}>
                <div className="text-xs text-center text-slate-500 mb-1">{day}</div>
                <div className="text-sm text-slate-100 text-center">{person.daily_target_minutes?.[i] ?? '–'}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Änderungen der Arbeitswoche gelten ab einem Stichtag — Pflege im Reiter „Zeiterfassung" unter „Arbeitszeitmodell".
          </div>
        </div>
      </Section>

      <Section title="Konditionen" slug="addressbook.section.konditionen" access={access} editing={editing} write={w('addressbook.section.konditionen')}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <Field label="Stundensatz (€)">
            {can('addressbook.section.konditionen')
              ? <input type="number" step="0.01" className={inp} value={form.hourly_rate ?? ''} onChange={(e) => setField('hourly_rate', e.target.value === '' ? null : Number(e.target.value))} />
              : <ReadOnly value={person.hourly_rate != null ? `${person.hourly_rate.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '–'} />}
          </Field>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs uppercase tracking-wider text-slate-500">Urlaubsanspruch pro Jahr</label>
            {canEditQuotas && (
              <button type="button" onClick={addYear}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-brand-600 hover:bg-brand-500 text-primary-foreground text-xs">
                <Plus size={12} /> Jahr hinzufügen
              </button>
            )}
          </div>
          {quotasByYear.length === 0
            ? <div className="text-sm text-slate-500">
                Keine Urlaubs-Eintraege.
                {canEditQuotas && <span className="ml-1">Klick „Jahr hinzufügen" um Anspruch fürs aktuelle Jahr zu setzen.</span>}
              </div>
            : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left py-1">Jahr</th>
                    <th className="text-right py-1">Anspruch (Tage)</th>
                    <th className="text-right py-1">Übertrag (Tage)</th>
                    <th className="text-right py-1">Gesamt</th>
                  </tr>
                </thead>
                <tbody>
                  {quotasByYear.map(([year, q]) => (
                    <tr key={year} className="border-t border-slate-800">
                      <td className="py-1 text-slate-200">{year}</td>
                      <td className="py-1 text-right">
                        {canEditQuotas
                          ? <input type="number" min={0} max={365} step={0.5}
                              value={q.total}
                              onChange={(e) => setQuota(year, { total: Number(e.target.value) })}
                              className="w-20 h-7 px-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-right tabular-nums" />
                          : <span className="tabular-nums text-slate-100">{fmtTage(q.total)}</span>}
                      </td>
                      <td className="py-1 text-right">
                        {canEditQuotas
                          ? <input type="number" min={0} max={365} step={0.5}
                              value={q.carry}
                              onChange={(e) => setQuota(year, { carry: Number(e.target.value) })}
                              className="w-20 h-7 px-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-right tabular-nums" />
                          : <span className="tabular-nums text-slate-400">{fmtTage(q.carry)}</span>}
                      </td>
                      <td className="py-1 text-right tabular-nums text-slate-100 font-semibold">{fmtTage(q.total + q.carry)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          {canEditQuotas && quotasByYear.length > 0 && (
            <div className="mt-1 text-[11px] text-slate-500">
              Änderungen werden zusammen mit den Stammdaten oben über „Speichern" persistiert.
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}

// =========================== TAB: Zugriffe ===========================

function sameIdSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

function ZugriffeTab({ person, form, setField, editing, access, personId, registry, onAction }: {
  person: PersonDetail; form: Partial<PersonDetail>;
  setField: (k: keyof PersonDetail, v: unknown) => void;
  editing: boolean; access: FieldAccess; personId: string;
  /** Sammelstelle des globalen Speichern-Knopfs (1.42): Gruppen + Modul-Rechte melden sich an. */
  registry: SaveRegistry;
  onAction: () => void;
}) {
  const tabRead = (access['addressbook.tab.zugriffe'] ?? 'none') !== 'none';
  const tabWrite = (access['addressbook.tab.zugriffe'] ?? 'none') === 'write';
  const canEdit = editing && tabWrite;
  const [allGroups, setAllGroups] = useState<AddressbookGroup[]>([]);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  // Fein-ACL (Joerg 2026-08-20): pro Modul-Zeile kann der Admin die Fein-Haekchen
  // aufklappen — Matrix erscheint dann unter der Modul-Zeile.
  const [expandedFein, setExpandedFein] = useState<Set<string>>(new Set());
  const toggleFein = (slug: string) => setExpandedFein((prev) => {
    const s = new Set(prev);
    if (s.has(slug)) s.delete(slug); else s.add(slug);
    return s;
  });

  useEffect(() => {
    if (!tabRead) return;
    api.get<{ success: boolean; data: AddressbookGroup[] }>('/admin/adressbuch/groups')
      .then((r) => { setAllGroups(r.data ?? []); setGroupsError(null); })
      .catch((e: unknown) => { setAllGroups([]); setGroupsError(`Gruppen nicht geladen: ${(e as Error).message || 'Verbindung fehlgeschlagen'}`); });
  }, [tabRead]);

  // Dokument-Navigation 1.42 (Jörg 2026-09-29, R5): Haken und Level ändern sich nur in
  // der Arbeitskopie — geschrieben wird mit dem globalen „Speichern" der Akte. Ersetzt das
  // Sofort-Speichern vom 19./20.05.2026.
  const savedGroupIds = useMemo(() => new Set((person.groups ?? []).map((g) => g.id)), [person.groups]);
  const [pendingGroups, setPendingGroups] = useState<Set<string> | null>(null);
  const [pendingLevels, setPendingLevels] = useState<Map<string, string>>(new Map());
  useEffect(() => { setPendingGroups(null); setPendingLevels(new Map()); }, [person]);
  const groupIds = pendingGroups ?? savedGroupIds;
  const groupsDirty = pendingGroups != null && !sameIdSet(pendingGroups, savedGroupIds);
  const zugriffeDirty = groupsDirty || pendingLevels.size > 0;
  const savedPersonLevel = (moduleSlug: string): string =>
    (person.access ?? []).find((a) => a.module_slug === moduleSlug)?.sources.find((s) => s.type === 'person')?.level ?? 'none';

  useSaveParticipant(registry, {
    id: 'zugriffe',
    label: 'Zugriffe (Gruppen, Modul-Rechte)',
    dirty: zugriffeDirty,
    flush: async () => {
      const failed: string[] = [];
      if (groupsDirty && pendingGroups) {
        try {
          await api.put(`/admin/adressbuch/persons/${personId}/groups`, { groupIds: Array.from(pendingGroups) });
          setPendingGroups(null);
        } catch (e) { failed.push(`Gruppen: ${(e as Error).message}`); }
      }
      for (const [slug, level] of pendingLevels) {
        try {
          await api.put(`/admin/adressbuch/persons/${personId}/access/${slug}`, { level });
          setPendingLevels((prev) => { const m = new Map(prev); m.delete(slug); return m; });
        } catch (e) { failed.push(`${slug}: ${(e as Error).message}`); }
      }
      onAction();
      if (failed.length > 0) throw new Error(failed.join(' · '));
      return true;
    },
    discard: () => { setPendingGroups(null); setPendingLevels(new Map()); },
  });

  if (!tabRead) return null;

  const toggleGroup = (groupId: string, isMember: boolean) => {
    if (!canEdit) return;
    const next = new Set(groupIds);
    if (isMember) next.delete(groupId); else next.add(groupId);
    setPendingGroups(next);
  };
  const setModuleLevel = (moduleSlug: string, level: string) => {
    if (!canEdit) return;
    setPendingLevels((prev) => {
      const m = new Map(prev);
      if (level === savedPersonLevel(moduleSlug)) m.delete(moduleSlug); else m.set(moduleSlug, level);
      return m;
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Rolle</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Aktuelle Rolle (users.role)">
            {canEdit ? (
              <select className={inp} value={form.user_role ?? 'handwerker'} onChange={(e) => setField('user_role', e.target.value)}>
                {ALL_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] ?? r}</option>)}
              </select>
            ) : <ReadOnly value={ROLE_LABEL[person.user_role ?? ''] ?? person.user_role} />}
          </Field>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Rolle = Single-Wert in <code>users.role</code>. Modul-Zugriffe ergeben sich aus Default-ACL + Gruppen + Person-Overrides.
          Rolle, Gruppen-Haken und Person-Overrides werden gemeinsam mit „Speichern" oben gesichert — nichts davon schreibt sofort.
        </p>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4" data-test-anchor="gruppen" data-dirty={groupsDirty ? '1' : '0'}>
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Adressbuch-Gruppen</h3>
        {groupsError && <div className="text-sm text-rose-300 mb-2" data-test-anchor="ladefehler">{groupsError}</div>}
        {allGroups.length === 0
          ? <div className="text-sm text-slate-500">Keine Gruppen konfiguriert.</div>
          : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {allGroups.map((g) => {
                const isMember = groupIds.has(g.id);
                const changed = isMember !== savedGroupIds.has(g.id);
                return (
                  <label key={g.id} className={`flex items-center gap-2 px-3 py-2 rounded border bg-slate-900/60 ${canEdit ? 'cursor-pointer hover:border-slate-600' : 'cursor-default'} ${changed ? 'border-amber-400' : isMember ? 'border-brand-500/40' : 'border-slate-800'}`}>
                    <input type="checkbox" checked={isMember}
                           disabled={!canEdit}
                           onChange={() => toggleGroup(g.id, isMember)} />
                    <span className="text-sm">{g.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        <div className="text-xs text-slate-500 mt-2">
          Multi-Mitgliedschaft erlaubt — z.B. „Chef-mit-Stiefeln" = Geschäftsführung + Handwerker.
          {canEdit && ' Geändert wird erst mit „Speichern" oben.'}
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Modul-Zugriffe (ACL)</h3>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 bg-slate-800/40">
            <tr>
              <th className="text-left py-2 px-3">Modul</th>
              <th className="text-left py-2 px-3">Person-Override</th>
              <th className="text-left py-2 px-3">Effektiv</th>
              <th className="text-left py-2 px-3">Quelle</th>
              <th className="text-left py-2 px-3 w-16">Fein</th>
            </tr>
          </thead>
          <tbody>
            {(person.access ?? []).map((a) => {
              const personSource = a.sources.find((s) => s.type === 'person');
              const personLevel = pendingLevels.get(a.module_slug) ?? personSource?.level ?? 'none';
              const levelChanged = pendingLevels.has(a.module_slug);
              const otherSources = a.sources.filter((s) => s.type !== 'person');
              const effectiveSource = a.sources
                .filter((s) => s.level !== 'none')
                .sort((x, y) => {
                  const order = ['none', 'depositor', 'reader', 'tester', 'author', 'editor', 'admin'];
                  return order.indexOf(y.level) - order.indexOf(x.level);
                })[0];
              const isExpanded = expandedFein.has(a.module_slug);
              const feinLevel = ['reader', 'tester', 'author', 'editor', 'admin'].includes(a.effective_level)
                ? (a.effective_level as 'reader' | 'tester' | 'author' | 'editor' | 'admin')
                : undefined;
              return (
                <Fragment key={a.module_slug}>
                  <tr className="border-t border-slate-800">
                    <td className="py-2 px-3 text-slate-100">{a.module_slug}</td>
                    <td className="py-2 px-3">
                      {canEdit ? (
                        <select className={`${inp} !w-44 ${levelChanged ? 'border-amber-400 ring-2 ring-amber-400/40' : ''}`}
                                value={personLevel}
                                data-changed={levelChanged ? '1' : '0'}
                                onChange={(e) => setModuleLevel(a.module_slug, e.target.value)}>
                          <option value="none">— (erbt)</option>
                          <option value="reader">reader</option>
                          <option value="tester">tester</option>
                          <option value="author">author</option>
                          <option value="editor">editor</option>
                          <option value="admin">admin</option>
                        </select>
                      ) : (
                        <span className="text-slate-400">{personLevel === 'none' ? '— (erbt)' : personLevel}</span>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${LEVEL_BG[a.effective_level] ?? LEVEL_BG['none']}`}>
                        {a.effective_level}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs text-slate-500">
                      {effectiveSource
                        ? `${effectiveSource.type === 'person' ? 'Person-Override' : effectiveSource.type === 'group' ? `Gruppe "${effectiveSource.name}"` : 'Default'} (${effectiveSource.level})`
                        : otherSources.length > 0 ? `Default ${otherSources[0]?.level ?? 'none'}` : '–'}
                    </td>
                    <td className="py-2 px-3">
                      <button
                        type="button"
                        onClick={() => toggleFein(a.module_slug)}
                        className={`rounded border px-2 py-1 text-xs transition ${
                          isExpanded
                            ? 'border-brand-500 bg-brand-500/20 text-brand-200'
                            : 'border-slate-700 bg-slate-800/60 text-slate-400 hover:border-slate-500'
                        }`}
                        title="Fein-Rechte-Matrix ein-/ausklappen"
                      >
                        {isExpanded ? '▾ Rechte' : '▸ Rechte'}
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-t border-slate-800/50 bg-slate-950/40">
                      <td colSpan={5} className="p-3">
                        <ModulePermissionsMatrix
                          moduleSlug={a.module_slug}
                          subjectType="person"
                          subjectId={personId}
                          subjectLabel={`${person.first_name} ${person.last_name}`.trim()}
                          currentLevel={feinLevel}
                          onChanged={onAction}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        <div className="text-xs text-slate-500 mt-2">
          „— (erbt)" entfernt die Person-Zeile (Default + Gruppen greifen). Level-Hierarchie:
          <code className="mx-1">admin &gt; editor &gt; author &gt; tester &gt; reader &gt; depositor &gt; none</code>.
          Modul-Liste kommt dynamisch aus der Self-Registry.
          {canEdit && ' Geänderte Level werden erst mit „Speichern" oben geschrieben.'}
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Doc-Level-Zugriffe</h3>
        {(person.doc_shares ?? []).length === 0
          ? <div className="text-sm text-slate-500">Keine Doc-Shares.</div>
          : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left py-1">Modul</th>
                  <th className="text-left py-1">Doc-ID</th>
                  <th className="text-left py-1">Level</th>
                  <th className="text-left py-1">Erteilt am</th>
                </tr>
              </thead>
              <tbody>
                {(person.doc_shares ?? []).map((s) => (
                  <tr key={s.doc_id + s.granted_at} className="border-t border-slate-800">
                    <td className="py-1 text-slate-300">{s.module_slug}</td>
                    <td className="py-1 text-slate-400 font-mono text-xs">{s.doc_id.slice(0, 8)}…</td>
                    <td className="py-1"><span className={`text-xs px-2 py-0.5 rounded ${LEVEL_BG[s.level] ?? LEVEL_BG['none']}`}>{s.level}</span></td>
                    <td className="py-1 text-xs text-slate-500">{fmtDateTime(s.granted_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        <div className="text-xs text-slate-500 mt-2">Wird über die jeweiligen Modul-Tools (Akten-Freigabe, Bericht-Verteiler) gepflegt — hier nur Anzeige.</div>
      </div>
    </div>
  );
}

// =========================== TAB: Sicherheit ===========================

function SicherheitTab({ person, access, audit, personId, onAction }: {
  person: PersonDetail; access: FieldAccess; audit: AuditEntry[]; personId: string; onAction: () => void;
}) {
  const canWrite = (access['addressbook.tab.sicherheit'] ?? 'none') === 'write';
  const dlg = useDialogs();
  const [newPw, setNewPw] = useState('');
  const [newPwRepeat, setNewPwRepeat] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  // Einzel-Aktionen (Ausnahme E4): bestätigt, Fehler sichtbar — keine Hinweisfenster des Browsers.
  const fire = async (action: string, call: () => Promise<unknown>, confirmMsg?: string): Promise<void> => {
    if (confirmMsg && !(await dlg.confirm({ title: 'Sicherheit', message: confirmMsg, confirmLabel: 'Ausführen' }))) return;
    setBusy(action);
    try { await call(); onAction(); } catch (e) { await dlg.error('Aktion fehlgeschlagen', e); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Konto-Status</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <Field label="Aktiv">
            <ReadOnly value={person.active === false ? 'inaktiv' : 'aktiv'} />
          </Field>
          <Field label="Sperre">
            <span className={`inline-block text-xs px-2 py-0.5 rounded ${person.locked ? 'bg-rose-500/15 text-rose-300' : 'bg-brand-500/15 text-brand-300'}`}>
              {person.locked ? 'gesperrt' : 'nicht gesperrt'}
            </span>
          </Field>
          <Field label="Aktiv seit (Eintritt)">
            <ReadOnly value={fmtDate(person.employed_since)} />
          </Field>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <button disabled={busy !== null}
                    onClick={() => fire(person.locked ? 'unlock' : 'lock',
                      () => api.post(`/admin/adressbuch/persons/${personId}/security/${person.locked ? 'unlock' : 'lock'}`, {}),
                      `${person.locked ? 'Konto entsperren' : 'Konto sperren'} wirklich ausführen?`)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 disabled:opacity-40">
              {person.locked ? <LockOpen className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              {person.locked ? 'Entsperren' : 'Konto sperren'}
            </button>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Login</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <Field label="Letzter Login">
            <ReadOnly value={fmtDateTime(person.last_login_at)} />
          </Field>
          <Field label="Fehlversuche aktuell">
            <div className="flex items-center gap-3">
              <span className="text-slate-100 tabular-nums">{person.failed_attempts ?? 0}</span>
              {canWrite && (person.failed_attempts ?? 0) > 0 && (
                <button disabled={busy !== null}
                        onClick={() => fire('reset-fa',
                          () => api.post(`/admin/adressbuch/persons/${personId}/security/reset-failed-attempts`, {}))}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200">
                  <RefreshCcw className="w-3 h-3" /> Zähler zurücksetzen
                </button>
              )}
            </div>
          </Field>
          <Field label="Letzter Fehlversuch">
            <ReadOnly value={fmtDateTime(person.last_failed_at)} />
          </Field>
        </div>
      </div>

      {canWrite && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Passwort</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Neues Passwort">
              <input type="password" placeholder="mindestens 8 Zeichen" className={inp}
                     value={newPw} onChange={(e) => setNewPw(e.target.value)} />
            </Field>
            <Field label="Wiederholen">
              <input type="password" className={inp}
                     value={newPwRepeat} onChange={(e) => setNewPwRepeat(e.target.value)} />
            </Field>
            <div className="flex items-end">
              <button disabled={newPw.length < 8 || newPw !== newPwRepeat || busy !== null}
                      onClick={() => fire('pw-set',
                        () => api.post(`/admin/adressbuch/persons/${personId}/password`, { password: newPw })
                          .then(() => { setNewPw(''); setNewPwRepeat(''); }))}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-brand-600 hover:bg-brand-500 text-primary-foreground disabled:opacity-40">
                <KeyRound className="w-4 h-4" /> Passwort setzen
              </button>
            </div>
          </div>
          {newPw.length > 0 && newPwRepeat.length > 0 && newPw !== newPwRepeat && (
            <div className="text-xs text-rose-400">Die Passwörter stimmen nicht überein.</div>
          )}
          <div className="text-xs text-slate-500">
            Reset-Mail-Versand kommt in einer Folgewelle, sobald die Mailing-Infrastruktur steht.
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Zwei-Faktor-Authentifizierung (TOTP)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <Field label="Status">
            <span className={`inline-block text-xs px-2 py-0.5 rounded ${person.totp_enabled ? 'bg-brand-500/15 text-brand-300' : 'bg-slate-700/40 text-slate-400'}`}>
              {person.totp_enabled ? 'aktiv' : 'nicht eingerichtet'}
            </span>
          </Field>
          <Field label="Eingerichtet am">
            <ReadOnly value={fmtDateTime(person.totp_enabled_at)} />
          </Field>
          <div className="flex items-end">
            {canWrite && person.totp_enabled && (
              <button disabled={busy !== null}
                      onClick={() => fire('reset-totp',
                        () => api.post(`/admin/adressbuch/persons/${personId}/security/reset-totp`, {}),
                        'TOTP wirklich zurücksetzen? Der Mitarbeiter muss es danach neu einrichten.')}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100">
                <ShieldOff className="w-4 h-4" /> TOTP zurücksetzen
              </button>
            )}
          </div>
        </div>
        <div className="text-xs text-slate-500">Mitarbeiter aktiviert TOTP selbst unter „Profil → Sicherheit". Reset macht der Admin nur bei verlorenem Gerät.</div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Aktive Sitzungen ({(person.sessions ?? []).length})</h3>
          {canWrite && (person.sessions ?? []).length > 0 && (
            <button disabled={busy !== null}
                    onClick={() => fire('term-all',
                      () => api.post(`/admin/adressbuch/persons/${personId}/security/terminate-sessions`, {}),
                      'Alle Sitzungen wirklich beenden?')}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg bg-rose-500/20 hover:bg-rose-500/30 text-rose-200">
              <LogOut className="w-3 h-3" /> Alle abmelden
            </button>
          )}
        </div>
        {(person.sessions ?? []).length === 0
          ? <div className="text-sm text-slate-500">Keine aktiven Sitzungen.</div>
          : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left py-1">Gerät</th>
                  <th className="text-left py-1">Angemeldet seit</th>
                  <th className="text-left py-1">IP</th>
                  <th className="text-left py-1">Läuft ab</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(person.sessions ?? []).map((s) => (
                  <tr key={s.token_id} className="border-t border-slate-800">
                    <td className="py-1 text-slate-100">{s.user_agent?.slice(0, 80) ?? '–'}</td>
                    <td className="py-1 text-slate-400 whitespace-nowrap">{fmtDateTime(s.created_at)}</td>
                    <td className="py-1 text-slate-400 tabular-nums whitespace-nowrap">{s.ip ?? '–'}</td>
                    <td className="py-1 text-slate-400 whitespace-nowrap">{fmtDateTime(s.expires_at)}</td>
                    <td className="py-1 text-right">
                      {canWrite && (
                        <button disabled={busy !== null}
                                onClick={() => fire(`term-${s.token_id}`,
                                  () => api.delete(`/admin/adressbuch/persons/${personId}/sessions/${s.token_id}`),
                                  'Diese Sitzung wirklich beenden?')}
                                className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-rose-500/30 text-slate-200">
                          abmelden
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Audit-Spur (letzte 20)</h3>
        {audit.length === 0
          ? <div className="text-sm text-slate-500">Keine Eintraege.</div>
          : (
            <table className="w-full text-xs">
              <thead className="text-slate-500 uppercase">
                <tr>
                  <th className="text-left py-1 pr-3">Zeit</th>
                  <th className="text-left py-1 pr-3">Akteur</th>
                  <th className="text-left py-1">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((e) => (
                  <tr key={e.id} className="border-t border-slate-800 align-top">
                    <td className="py-1 pr-3 text-slate-400 whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                    <td className="py-1 pr-3 text-slate-200 whitespace-nowrap">{e.actor_full_name}</td>
                    <td className="py-1 text-slate-300"><AuditActionCell entry={e} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}

// =========================== TAB: Akte (5 Sub-Tabs) ===========================

interface BalanceSummary { total_saldo: number; week_netto: number; month_netto: number; last_day: string | null }
// StampEntry entfernt 2026-08-08 — Zeitkonto-Ansicht kommt aus Federation-Komponente.
// Zeitkonto-Aggregat (Joerg 2026-08-09) — /admin/timekeeping-summary/:userId.
// Zerlegt den Gesamt-Saldo in Uebernahme + akkumuliertes daily_balances-Saldo,
// damit der Admin sieht "was kommt aus dem Altsystem, was ist neu gestempelt".
interface TimekeepingSummary {
  settings: {
    hire_date: string | null;
    exit_date: string | null;
    start_saldo_minutes: number;
    weekly_target_minutes: number;
    daily_target_minutes: number[];
    /** MZ (2026-08-27): Arbeitszeitmodell. */
    employment_type?: string;
    daily_break_minutes?: number[];
  };
  saldo: {
    start_saldo_minutes: number;
    balance_sum_minutes: number;
    total_minutes: number;
    days_counted: number;
    last_day: string | null;
  };
  leave: {
    per_year: Array<{ year: number; total_days: number; carryover_days: number; used_days: number; remaining_days: number }>;
    remaining_total_days: number;
  };
  /** MZ-3: nur bei employment_type='minijob' befuellt. */
  minijob?: {
    month_minutes: number;
    earnings_cents: number | null;
    limit_cents: number;
    warn_percent: number;
  } | null;
}

// ============================================================================
// MZ (2026-08-27, Joerg): Arbeitszeitmodell-Karte im Zeiterfassung-Sub-Tab.
// Eigenstaendige Laden/Speichern-Logik ueber die Modul-API (approve-Recht,
// Entscheid F4) — unabhaengig vom Personen-Formular-Save. Plan:
// 02 Projekte/Probst Kundenportal/Zeiterfassung-Arbeitszeitmodelle.md
// ============================================================================

const MZ_WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
// BA (2026-08-28, Joerg): Beschaeftigungsarten v2. 'standard' bleibt als
// Anzeige-Fallback fuer noch nicht migrierte Instanzen.
const MZ_EMPLOYMENT_LABEL: Record<string, string> = {
  vollzeit: 'Vollzeit',
  teilzeit: 'Teilzeit',
  werkstudent: 'Werkstudent',
  azubi: 'Azubi',
  minijob: 'Minijob',
};
const MZ_EMPLOYMENT_LABEL_LEGACY: Record<string, string> = { ...MZ_EMPLOYMENT_LABEL, standard: 'Vollzeit' };

/** 1.29.0 Zeiterfassung (Pausenregelung): Pausenfenster, Wochentag 1 = Mo … 7 = So. */
interface BreakWin { weekday: number; start: string; end: string; label: string }
type BreakMode = 'standard' | 'eigene' | 'keine';
interface BreakRow { start: string; end: string; label: string }

interface MzEmploymentData {
  employment_type: string;
  hourly_wage_cents: number | null;
  monthly_earnings_limit_cents: number | null;
  daily_break_minutes: number[];
  /** 1.29.0: eigene Pausenfenster der heute gültigen Basis — null = Betriebsregel/Minuten, [] = keine. */
  break_windows?: BreakWin[] | null;
  tenant_fixed_breaks?: { enabled: boolean; valid_from: string | null; windows: BreakWin[] };
  effective_break_today?: { source: 'eigene' | 'betrieb' | 'minuten' | 'keine'; windows: BreakWin[]; minutes: number };
  daily_target_minutes?: number[] | null;
  /** BA-Fix: Von/Bis der heute gueltigen Basis-Periode (NULL = Gleitzeit). */
  daily_start_times?: (string | null)[] | null;
  daily_end_times?: (string | null)[] | null;
  default_limit_cents: number;
  /** AZM-H8: heute gueltige Werte + Beschaeftigungs-Historie (Perioden). */
  effective?: {
    employment_type: string;
    hourly_wage_cents: number | null;
    monthly_earnings_limit_cents: number | null;
    target_minutes_today: number;
    break_minutes_today: number;
    zeit_modus?: string;
  };
  periods?: MzEmploymentPeriodRow[];
}
interface MzEmploymentPeriodRow {
  id: string;
  valid_from: string;
  valid_to: string | null;
  employment_type: string;
  hourly_wage_cents: number | null;
  monthly_earnings_limit_cents: number | null;
  zeit_modus?: string;
}
interface MzPeriodRow {
  id: string;
  label: string;
  valid_from: string;
  valid_to: string | null;
  daily_target_minutes: number[];
  daily_break_minutes: number[];
  break_windows?: BreakWin[] | null;
}

// ---- 1.29.0 Pausenregelung (Jörg 2026-10-20): Auswahl „Feste Pausen“ + Fenster-Editor.
// Editor ist eine schlanke Kopie des Bausteins aus zeiterfassung/AdminPausenregelungPage
// (kein statischer Import zwischen Modulen).
function breakModeOf(w: BreakWin[] | null | undefined): BreakMode {
  if (w == null) return 'standard';
  return w.length === 0 ? 'keine' : 'eigene';
}
function windowsToRows(w: BreakWin[] | null | undefined): Record<number, BreakRow[]> {
  const out: Record<number, BreakRow[]> = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };
  for (const x of w ?? []) if (x.weekday >= 1 && x.weekday <= 7) out[x.weekday]!.push({ start: x.start, end: x.end, label: x.label });
  for (let d = 1; d <= 7; d += 1) out[d]!.sort((a, b) => a.start.localeCompare(b.start));
  return out;
}
function rowsToWindows(rows: Record<number, BreakRow[]>): BreakWin[] {
  const out: BreakWin[] = [];
  for (let d = 1; d <= 7; d += 1) for (const r of rows[d] ?? []) out.push({ weekday: d, start: r.start, end: r.end, label: r.label.trim() });
  return out;
}
function describeBreakWindows(w: BreakWin[] | null | undefined): string {
  const rows = windowsToRows(w);
  const parts: string[] = [];
  for (let d = 1; d <= 7; d += 1) {
    const r = rows[d] ?? [];
    if (r.length > 0) parts.push(`${MZ_WEEKDAYS[d - 1]}: ${r.map((x) => `${x.label} ${x.start}–${x.end}`).join(' · ')}`);
  }
  return parts.length > 0 ? parts.join(' / ') : 'keine';
}
function breakWindowsForMode(mode: BreakMode, rows: Record<number, BreakRow[]>): BreakWin[] | null {
  if (mode === 'standard') return null;
  if (mode === 'keine') return [];
  return rowsToWindows(rows);
}
function describeEffectiveBreak(e: MzEmploymentData['effective_break_today'] | undefined): string {
  if (!e) return '–';
  if (e.source === 'betrieb') return `Pausenregelung des Betriebs: ${describeBreakWindows(e.windows)}`;
  if (e.source === 'eigene') return `Eigene Pausenzeiten: ${describeBreakWindows(e.windows)}`;
  if (e.source === 'keine') return 'Keine festen Pausen';
  return e.minutes > 0 ? `${e.minutes} Minuten (feste Minuten)` : 'Keine festen Pausen';
}

function BreakModeSelect({ mode, onChange, tenantEnabled, disabled }: {
  mode: BreakMode; onChange?: (m: BreakMode) => void; tenantEnabled: boolean; disabled?: boolean;
}) {
  return (
    <select className={inp} value={mode} disabled={disabled || !onChange} onChange={(e) => onChange?.(e.target.value as BreakMode)} data-test-anchor="pausen-auswahl">
      <option value="standard">{tenantEnabled ? 'Pausenregelung des Betriebs' : 'Feste Minuten je Wochentag (bisher)'}</option>
      <option value="eigene">Eigene Pausenzeiten</option>
      <option value="keine">Keine festen Pausen</option>
    </select>
  );
}

function BreakWindowsEditor({ rows, onChange, disabled }: {
  rows: Record<number, BreakRow[]>; onChange?: (next: Record<number, BreakRow[]>) => void; disabled?: boolean;
}) {
  const ro = disabled || !onChange;
  const set = (d: number, list: BreakRow[]) => onChange?.({ ...rows, [d]: list });
  return (
    <div className="space-y-1.5">
      {[1, 2, 3, 4, 5, 6, 7].map((d) => {
        const list = rows[d] ?? [];
        return (
          <div key={d} className="flex flex-wrap items-center gap-1.5 text-xs" data-test-anchor={`pausen-tag-${d}`}>
            <span className="w-7 uppercase text-slate-500">{MZ_WEEKDAYS[d - 1]}</span>
            {list.map((r, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-1.5 py-1">
                <input className="w-20 bg-transparent text-slate-100 outline-none" value={r.label} placeholder="Name" disabled={ro}
                       onChange={(e) => set(d, list.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                <input type="time" className="bg-transparent text-slate-100 outline-none" value={r.start} disabled={ro}
                       onChange={(e) => set(d, list.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
                <span className="text-slate-500">–</span>
                <input type="time" className="bg-transparent text-slate-100 outline-none" value={r.end} disabled={ro}
                       onChange={(e) => set(d, list.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
                {!ro && <button type="button" className="text-rose-400 px-1" aria-label="Pause entfernen" onClick={() => set(d, list.filter((_, j) => j !== i))}>×</button>}
              </span>
            ))}
            {!ro && (
              <button type="button" className="px-1.5 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800"
                      onClick={() => set(d, [...list, { start: '12:00', end: '12:30', label: list.length === 0 ? 'Frühstück' : 'Mittag' }])}>+ Pause</button>
            )}
            {!ro && d >= 2 && d <= 5 && (rows[1]?.length ?? 0) > 0 && (
              <button type="button" className="px-1.5 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800"
                      onClick={() => set(d, (rows[1] ?? []).map((x) => ({ ...x })))}>wie Mo</button>
            )}
            {list.length === 0 && <span className="text-slate-500">keine</span>}
          </div>
        );
      })}
    </div>
  );
}

async function mzApi(method: string, path: string, body?: unknown): Promise<unknown> {
  const r = await fetch(`/api/modules/zeiterfassung${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || (j as { success?: boolean }).success === false) {
    throw new Error((j as { error?: string }).error ?? `HTTP ${r.status}`);
  }
  return (j as { data?: unknown }).data;
}

function centsToEuroStr(c: number | null): string {
  return c == null ? '' : (c / 100).toFixed(2).replace('.', ',');
}
function euroStrToCents(s: string): number | null {
  const t = s.trim().replace(',', '.');
  if (!t) return null;
  const n = parseFloat(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

function MinutesRow({ values, onChange, disabled }: {
  values: number[]; onChange?: (next: number[]) => void; disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-7 gap-1.5">
      {MZ_WEEKDAYS.map((wd, i) => (
        <label key={wd} className="text-center">
          <span className="block text-[10px] uppercase text-slate-500">{wd}</span>
          <input
            type="number" min={0} step={5} disabled={disabled || !onChange}
            value={values[i] ?? 0}
            onChange={(e) => {
              if (!onChange) return;
              const next = [...values];
              next[i] = e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0);
              onChange(next);
            }}
            className="w-full px-1 py-1.5 text-xs text-center rounded bg-slate-800 border border-slate-700 text-slate-100 disabled:opacity-60"
          />
        </label>
      ))}
    </div>
  );
}

/**
 * MZ-7 (2026-08-27, Joerg): Teilzeit-Hinweis am Urlaubskonto. Der
 * Jahresanspruch wird in PERSOENLICHEN Arbeitstagen gepflegt — bei einer
 * 2-Tage-Woche entsprechen 30 Vollzeit-Tage nur 12 Tagen (30 x 2/5).
 * Ohne diesen Hinweis traegt das Buero gedankenlos den Vollzeit-Wert ein
 * und der Mitarbeiter bekommt 15 Wochen Urlaub. Inklusive Umrechner
 * (nur im Edit-Modus): Vollzeit-Wert rein -> Anspruch fuers aktuelle
 * Jahr wird auf halbe Tage gerundet uebernommen.
 */
function TeilzeitAnspruchHinweis({ dtm, canConvert, onApply }: {
  dtm: number[] | null | undefined;
  canConvert: boolean;
  onApply: (days: number) => void;
}) {
  const [vollzeit, setVollzeit] = useState('30');
  const workdays = (dtm ?? []).filter((n) => (n ?? 0) > 0).length;
  if (!dtm || workdays === 0 || workdays >= 5) return null;
  const parsed = parseFloat(vollzeit.replace(',', '.'));
  const converted = Number.isFinite(parsed) && parsed > 0
    ? Math.round((parsed * workdays / 5) * 2) / 2
    : null;
  return (
    <div className="mb-2 px-3 py-2 rounded-lg bg-amber-950/30 border border-amber-900/50 text-xs text-amber-200 space-y-1.5">
      <div>
        Teilzeit-Profil ({workdays} Arbeitstag{workdays === 1 ? '' : 'e'}/Woche): Anspruch bitte in{' '}
        <strong>persönlichen Arbeitstagen</strong> pflegen — 30 Vollzeit-Tage entsprechen hier{' '}
        {(Math.round((30 * workdays / 5) * 2) / 2).toLocaleString('de-DE')} Tagen.
      </div>
      {canConvert && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5">
            Vollzeit-Anspruch:
            <input
              type="text" inputMode="decimal" value={vollzeit}
              onChange={(e) => setVollzeit(e.target.value)}
              className="w-16 h-7 px-2 rounded bg-slate-900 border border-amber-900/60 text-slate-100 text-right tabular-nums"
            />
          </label>
          <button
            type="button" disabled={converted == null}
            onClick={() => { if (converted != null) onApply(converted); }}
            className="px-2 py-1 rounded border border-amber-800/60 bg-amber-900/40 hover:bg-amber-900/70 text-amber-100 disabled:opacity-40"
          >
            → {converted != null ? converted.toLocaleString('de-DE') : '–'} Tage für dieses Jahr übernehmen
          </button>
        </div>
      )}
    </div>
  );
}

function ArbeitszeitmodellCard({ userId, canWrite, showEditHint = false, registry, minijob, onChanged }: {
  userId: string;
  canWrite: boolean;
  /** BA: true wenn der User schreiben DUERFTE, aber der Bearbeiten-Modus
   *  der Akte aus ist — dann kurzer Hinweis statt stiller grauer Felder. */
  showEditHint?: boolean;
  /** BA: Sammelstelle des globalen Speichern-Knopfs — offene Karten-Aenderungen
   *  werden dort mitgesichert (Joerg 2026-08-28: "Gilt nicht der allgemeine
   *  Speichern-Knopf?"). 1.42: über useSaveParticipant statt Ref. */
  registry?: SaveRegistry;
  minijob: TimekeepingSummary['minijob'];
  onChanged: () => void;
}) {
  const [data, setData] = useState<MzEmploymentData | null>(null);
  const [periods, setPeriods] = useState<MzPeriodRow[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Editier-Zustand Grunddaten
  const [empType, setEmpType] = useState('standard');
  const [wage, setWage] = useState('');
  const [limit, setLimit] = useState('');
  const [breaks, setBreaks] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]);
  // AZM-H8: Aenderungen gelten ab Stichtag (Perioden-Split im Backend) —
  // Rueckwirkung erlaubt, wird aber deutlich gewarnt.
  const [effFrom, setEffFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [empPeriods, setEmpPeriods] = useState<MzEmploymentPeriodRow[]>([]);
  const [targets, setTargets] = useState<number[]>([480, 480, 480, 480, 480, 0, 0]);
  const [confirmEmpDelete, setConfirmEmpDelete] = useState<string | null>(null);
  // BA (2026-08-28): Pflichtwahl bei Nicht-Vollzeit — Gleitzeit oder feste
  // Tagesuhrzeiten; bei festen Zeiten Von/Bis je Wochentag (historisiert an
  // den Arbeitszeiten-Perioden).
  const [zeitModus, setZeitModus] = useState<'gleitzeit' | 'feste_zeiten'>('gleitzeit');
  // BA: geladener Pausen-Stand — "Modell speichern" schickt dailyBreakMinutes
  // nur bei echter Aenderung mit (sonst erzeugt jeder Klick einen unnoetigen
  // Arbeitswochen-Split).
  const [loadedBreaks, setLoadedBreaks] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]);
  // 1.29.0 Pausenregelung: Auswahl „Feste Pausen“ (Betriebsregel/Minuten · eigene Fenster · keine).
  const [breakMode, setBreakMode] = useState<BreakMode>('standard');
  const [ownRows, setOwnRows] = useState<Record<number, BreakRow[]>>(windowsToRows(null));
  const [loadedBreakWindows, setLoadedBreakWindows] = useState<BreakWin[] | null>(null);
  const [tenantFixed, setTenantFixed] = useState<{ enabled: boolean; windows: BreakWin[] }>({ enabled: false, windows: [] });
  const [loadedModel, setLoadedModel] = useState<{ empType: string; zeitModus: string; wage: string; limit: string }>({ empType: 'vollzeit', zeitModus: 'gleitzeit', wage: '', limit: '' });
  const [loadedTargets, setLoadedTargets] = useState<number[]>([480, 480, 480, 480, 480, 0, 0]);
  const [loadedTimes, setLoadedTimes] = useState<{ start: string[]; end: string[] }>({ start: ['', '', '', '', '', '', ''], end: ['', '', '', '', '', '', ''] });
  const [startTimes, setStartTimes] = useState<string[]>(['', '', '', '', '', '', '']);
  const [endTimes, setEndTimes] = useState<string[]>(['', '', '', '', '', '', '']);
  // Perioden-Formular (dient Anlegen UND nachtraeglichem Bearbeiten —
  // editPeriodId gesetzt = Bearbeiten-Modus, PUT statt POST; Joerg 2026-09-16)
  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [editPeriodId, setEditPeriodId] = useState<string | null>(null);
  const [pLabel, setPLabel] = useState('');
  const [pFrom, setPFrom] = useState('');
  const [pTo, setPTo] = useState('');
  const [pTargets, setPTargets] = useState<number[]>([480, 480, 480, 480, 480, 0, 0]);
  const [pBreaks, setPBreaks] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]);
  const [pBreakMode, setPBreakMode] = useState<BreakMode>('standard');
  const [pOwnRows, setPOwnRows] = useState<Record<number, BreakRow[]>>(windowsToRows(null));
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [emp, per] = await Promise.all([
        mzApi('GET', `/admin/employment/${userId}`) as Promise<MzEmploymentData>,
        mzApi('GET', `/admin/schedule-periods/${userId}`) as Promise<MzPeriodRow[]>,
      ]);
      setData(emp);
      // AZM-H8-Revision (Joerg 2026-09-16, DEB01-Fund Moritz): Basisprofil +
      // "Arbeitszeiten ab"-Splits werden wieder ANGEZEIGT (mit Basis-Kennung
      // und Rueckwirkungs-Warnung im Loeschen-Confirm) — der alte Komplett-
      // Filter machte ein FALSCH uebernommenes Basisprofil unloeschbar: es
      // gewann per spaeterem valid_from gegen jede korrekte Werkstudenten-
      // Periode, und niemand sah warum.
      setPeriods(per ?? []);
      setEmpType(emp.employment_type ?? 'standard');
      setWage(centsToEuroStr(emp.hourly_wage_cents));
      setLimit(centsToEuroStr(emp.monthly_earnings_limit_cents));
      setBreaks(emp.daily_break_minutes ?? [0, 0, 0, 0, 0, 0, 0]);
      setLoadedBreaks(emp.daily_break_minutes ?? [0, 0, 0, 0, 0, 0, 0]);
      const bw = emp.break_windows ?? null;
      setBreakMode(breakModeOf(bw));
      setOwnRows(windowsToRows(bw));
      setLoadedBreakWindows(bw);
      setTenantFixed({ enabled: emp.tenant_fixed_breaks?.enabled === true, windows: emp.tenant_fixed_breaks?.windows ?? [] });
      setEmpPeriods(emp.periods ?? []);
      const dtm = emp.daily_target_minutes ?? null;
      setTargets(dtm && dtm.length === 7 ? dtm : dtm && dtm.length === 5 ? [...dtm, 0, 0] : [480, 480, 480, 480, 480, 0, 0]);
      setZeitModus(emp.effective?.zeit_modus === 'feste_zeiten' ? 'feste_zeiten' : 'gleitzeit');
      // BA-Fix: Uhrzeiten kommen jetzt direkt aus GET /admin/employment
      // (heute gueltige Basis-Periode).
      const st = emp.daily_start_times ?? null;
      const et = emp.daily_end_times ?? null;
      const stArr = st && st.length === 7 ? st.map((x) => x ?? '') : ['', '', '', '', '', '', ''];
      const etArr = et && et.length === 7 ? et.map((x) => x ?? '') : ['', '', '', '', '', '', ''];
      setStartTimes(stArr);
      setEndTimes(etArr);
      const dtmArr = dtm && dtm.length === 7 ? dtm : dtm && dtm.length === 5 ? [...dtm, 0, 0] : [480, 480, 480, 480, 480, 0, 0];
      setLoadedModel({
        empType: emp.employment_type ?? 'vollzeit',
        zeitModus: emp.effective?.zeit_modus === 'feste_zeiten' ? 'feste_zeiten' : 'gleitzeit',
        wage: centsToEuroStr(emp.hourly_wage_cents),
        limit: centsToEuroStr(emp.monthly_earnings_limit_cents),
      });
      setLoadedTargets(dtmArr);
      setLoadedTimes({ start: stArr, end: etArr });
    } catch (e) {
      setMsg(`Fehler beim Laden: ${(e as Error).message}`);
    }
  }, [userId]);
  useEffect(() => { void load(); }, [load]);

  async function saveBase(): Promise<boolean> {
    setBusy(true); setMsg(null);
    try {
      // AZM-H8: Aenderung = neue Periode ab "Gilt ab" (Backend schliesst die
      // Vorperiode) — nichts wird mehr ueberschrieben.
      await mzApi('PUT', `/admin/employment/${userId}`, {
        employmentType: empType,
        // BA-Fix: Lohn/Grenze sind Minijob-Groessen — bei anderen Arten
        // explizit null (sonst landete die vorbelegte globale Grenze als
        // Override in der Periode, "Werkstudent mit 650-EUR-Grenze").
        hourlyWageCents: empType === 'minijob' ? euroStrToCents(wage) : null,
        monthlyEarningsLimitCents: empType === 'minijob' ? euroStrToCents(limit) : null,
        dailyBreakMinutes: JSON.stringify(breaks) !== JSON.stringify(loadedBreaks) ? breaks : undefined,
        // 1.29.0: Pausenfenster-Auswahl — null = Betriebsregel/Minuten, [] = keine, [...] = eigene.
        breakWindows: (() => {
          const next = breakWindowsForMode(breakMode, ownRows);
          return JSON.stringify(next) !== JSON.stringify(loadedBreakWindows ?? null) ? next : undefined;
        })(),
        effectiveFrom: effFrom,
        // BA: Vollzeit arbeitet implizit mit Gleitzeit; sonst Pflichtwahl.
        zeitModus: empType === 'vollzeit' ? 'gleitzeit' : zeitModus,
      });
      setMsg('Gespeichert.');
      await load();
      onChanged();
      return true;
    } catch (e) {
      setMsg(`Fehler: ${(e as Error).message}`);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function saveTargets(): Promise<boolean> {
    setBusy(true); setMsg(null);
    try {
      const fixed = empType !== 'vollzeit' && zeitModus === 'feste_zeiten';
      await mzApi('PUT', `/admin/employee-settings/${userId}`, {
        dailyTargetMinutes: targets,
        effectiveFrom: effFrom,
        dailyStartTimes: fixed ? startTimes.map((x) => x || null) : null,
        dailyEndTimes: fixed ? endTimes.map((x) => x || null) : null,
      });
      setMsg('Arbeitswoche gespeichert.');
      await load();
      onChanged();
      return true;
    } catch (e) {
      setMsg(`Fehler: ${(e as Error).message}`);
      return false;
    } finally {
      setBusy(false);
    }
  }

  // BA: vom globalen Speichern-Knopf aufgerufen — sichert offene Karten-
  // Aenderungen (Stichtag = "Gilt ab" der Karte). Wirft bei Fehlschlag,
  // damit der globale Save nicht faelschlich "Gespeichert." meldet.
  const modelDirty = data != null && (empType !== loadedModel.empType
    || (empType !== 'vollzeit' && zeitModus !== loadedModel.zeitModus)
    || (empType === 'minijob' && (wage !== loadedModel.wage || limit !== loadedModel.limit))
    || JSON.stringify(breaks) !== JSON.stringify(loadedBreaks));
  const targetsDirty = data != null && (JSON.stringify(targets) !== JSON.stringify(loadedTargets)
    || (zeitModus === 'feste_zeiten' && (JSON.stringify(startTimes) !== JSON.stringify(loadedTimes.start) || JSON.stringify(endTimes) !== JSON.stringify(loadedTimes.end))));
  async function flushPendingChanges(): Promise<boolean> {
    if (!canWrite) return false;
    let saved = false;
    if (modelDirty) {
      if (!(await saveBase())) throw new Error('Arbeitszeitmodell konnte nicht gespeichert werden');
      saved = true;
    }
    if (targetsDirty) {
      if (!(await saveTargets())) throw new Error('Arbeitswoche konnte nicht gespeichert werden');
      saved = true;
    }
    return saved;
  }
  // 1.42: Anmeldung an der Sammelstelle — die Akte gilt als „ungespeichert", sobald die
  // Karte Änderungen hält; „Abbrechen"/Verwerfen lädt den Server-Stand neu.
  useSaveParticipant(registry, {
    id: 'arbeitszeitmodell',
    label: 'Arbeitszeitmodell',
    dirty: canWrite && (modelDirty || targetsDirty),
    flush: flushPendingChanges,
    discard: () => { void load(); },
  });

  async function deleteEmpPeriod(id: string): Promise<void> {
    setBusy(true); setMsg(null);
    try {
      await mzApi('DELETE', `/admin/employment-periods/${userId}/${id}`);
      setConfirmEmpDelete(null);
      setMsg('Wechsel zurückgenommen.');
      await load();
      onChanged();
    } catch (e) {
      setMsg(`Fehler: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function closePeriodForm(): void {
    setShowPeriodForm(false); setEditPeriodId(null);
    setPLabel(''); setPFrom(''); setPTo('');
    setPBreakMode('standard'); setPOwnRows(windowsToRows(null));
  }

  async function addPeriod(): Promise<boolean> {
    setBusy(true); setMsg(null);
    try {
      if (editPeriodId) {
        // Nachtraegliche Bearbeitung (Joerg 2026-09-16) — Backend AZM-H5
        // recomputet die Tage ueber alte UND neue Range, Audit-Eintrag inklusive.
        await mzApi('PUT', `/admin/schedule-periods/${userId}/${editPeriodId}`, {
          label: pLabel, validFrom: pFrom, validTo: pTo || null,
          dailyTargetMinutes: pTargets, dailyBreakMinutes: pBreaks,
          breakWindows: breakWindowsForMode(pBreakMode, pOwnRows),
        });
        setMsg('Zeitraum geändert — betroffene Tage werden neu berechnet.');
      } else {
        await mzApi('POST', `/admin/schedule-periods/${userId}`, {
          label: pLabel, validFrom: pFrom, validTo: pTo || null,
          dailyTargetMinutes: pTargets, dailyBreakMinutes: pBreaks,
          breakWindows: breakWindowsForMode(pBreakMode, pOwnRows),
        });
        setMsg('Zeitraum angelegt.');
      }
      closePeriodForm();
      await load();
      onChanged();
      return true;
    } catch (e) {
      setMsg(`Fehler: ${(e as Error).message}`);
      return false;
    } finally {
      setBusy(false);
    }
  }

  // Jörg 2026-10-15: Das Zeitraum-Formular hat keinen eigenen Speichern-Knopf mehr —
  // ein offenes Formular zählt als ungespeicherte Änderung der Akte, „Speichern“ oben
  // legt den Zeitraum an bzw. ändert ihn; unvollständig → sichtbarer Fehler statt stilles Überspringen.
  useSaveParticipant(registry, {
    id: 'arbeitszeitmodell-zeitraum',
    label: 'Zeitraum (Arbeitszeitmodell)',
    dirty: canWrite && showPeriodForm,
    flush: async () => {
      if (!pLabel.trim() || !pFrom) throw new Error('Zeitraum: Bezeichnung und „Von“ ausfüllen oder das Formular mit „Abbrechen“ schließen');
      if (!(await addPeriod())) throw new Error('Zeitraum konnte nicht gespeichert werden');
      return true;
    },
    discard: () => closePeriodForm(),
  });

  function startEditPeriod(p: MzPeriodRow): void {
    setEditPeriodId(p.id);
    setPLabel(p.label);
    setPFrom(p.valid_from.slice(0, 10));
    setPTo(p.valid_to ? p.valid_to.slice(0, 10) : '');
    setPTargets([...p.daily_target_minutes]);
    setPBreaks([...p.daily_break_minutes]);
    setPBreakMode(breakModeOf(p.break_windows));
    setPOwnRows(windowsToRows(p.break_windows));
    setShowPeriodForm(true);
  }

  async function deletePeriod(id: string): Promise<void> {
    setBusy(true); setMsg(null);
    try {
      await mzApi('DELETE', `/admin/schedule-periods/${userId}/${id}`);
      setConfirmDelete(null);
      setMsg('Zeitraum gelöscht.');
      await load();
      onChanged();
    } catch (e) {
      setMsg(`Fehler: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!data) return null;
  const limitCents = euroStrToCents(limit) ?? data.default_limit_cents;
  const pct = minijob && minijob.earnings_cents != null && minijob.limit_cents > 0
    ? Math.min(100, Math.round((minijob.earnings_cents / minijob.limit_cents) * 100))
    : null;

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Arbeitszeitmodell</h3>
        {showEditHint && (
          <span className="text-xs text-slate-500">Zum Ändern oben „Bearbeiten" wählen.</span>
        )}
        {canWrite && (
          <span className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Gilt ab
              <input type="date" className={`${inp} ml-1.5 inline-block w-auto`} value={effFrom}
                     onChange={(e) => setEffFrom(e.target.value)} />
            </label>
            {/* Jörg 2026-10-15 (DEB01 Moritz): nur noch EIN Speichern-Knopf — der der Akte.
                Die Karten-Knöpfe „Modell speichern“ / „Arbeitswoche speichern“ schickten
                verschiedene Felder (Pausen nur über „Modell“), die Pausen-Korrektur ging verloren. */}
            <span className="text-xs text-slate-500">Änderungen übernimmt „Speichern“ oben{effFrom ? ` (gilt ab ${fmtDate(effFrom)})` : ''}.</span>
          </span>
        )}
      </div>
      {canWrite && effFrom && effFrom < new Date().toISOString().slice(0, 10) && (
        <div className="text-xs rounded border border-amber-700/50 bg-amber-950/30 text-amber-200 px-2.5 py-1.5">
          Rückwirkende Änderung — die Zeitkonten ab dem {fmtDate(effFrom)} werden neu berechnet.
        </div>
      )}
      {msg && <div className={`text-xs ${msg.startsWith('Fehler') ? 'text-rose-300' : 'text-emerald-300'}`}>{msg}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <div className="text-xs text-slate-500 mb-1">Beschäftigungsart</div>
          <select className={inp} value={empType} disabled={!canWrite}
                  onChange={(e) => setEmpType(e.target.value)}>
            {Object.entries(MZ_EMPLOYMENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div className="text-xs text-slate-500 mt-1">Minijob: kein Zeitkonto, Verdienst-Überwachung aktiv.</div>
          {empType !== 'vollzeit' && (
            <div className="mt-2">
              <div className="text-xs text-slate-500 mb-1">Arbeitszeit-Regelung</div>
              <div className="flex gap-3 text-sm text-slate-200">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="ba-zeitmodus" checked={zeitModus === 'gleitzeit'}
                         disabled={!canWrite} onChange={() => setZeitModus('gleitzeit')} />
                  Gleitzeit
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="ba-zeitmodus" checked={zeitModus === 'feste_zeiten'}
                         disabled={!canWrite} onChange={() => setZeitModus('feste_zeiten')} />
                  Feste Uhrzeiten
                </label>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {zeitModus === 'feste_zeiten'
                  ? 'Von/Bis je Wochentag unten bei der Arbeitswoche pflegen — erscheint so in der Plantafel.'
                  : 'Nur die Stundenzahl zählt — die Plantafel zeigt z. B. „4 h".'}
              </div>
            </div>
          )}
        </div>
        {empType === 'minijob' && (
          <>
            <div>
              <div className="text-xs text-slate-500 mb-1">Stundenlohn (€)</div>
              <input type="text" inputMode="decimal" className={inp} value={wage} disabled={!canWrite}
                     placeholder="z.B. 13,90" onChange={(e) => setWage(e.target.value)} />
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Monatsgrenze (€)</div>
              <input type="text" inputMode="decimal" className={inp} value={limit} disabled={!canWrite}
                     placeholder={centsToEuroStr(data.default_limit_cents)} onChange={(e) => setLimit(e.target.value)} />
              <div className="text-xs text-slate-500 mt-1">Leer = Standard ({centsToEuroStr(data.default_limit_cents)} €).</div>
            </div>
          </>
        )}
      </div>

      {empType === 'minijob' && minijob && (
        <div className="rounded border border-slate-800 bg-slate-950/50 p-3">
          <div className="text-xs text-slate-400 mb-1.5">
            Dieser Monat: <span className="font-semibold text-slate-100">{fmtMinutes(minijob.month_minutes)}</span> gearbeitet
            {minijob.earnings_cents != null && (
              <> · <span className={`font-semibold ${pct != null && pct >= minijob.warn_percent ? 'text-amber-300' : 'text-slate-100'}`}>
                {centsToEuroStr(minijob.earnings_cents)} €
              </span> von {centsToEuroStr(limitCents)} €</>
            )}
          </div>
          {pct != null && (
            <div className="h-2 rounded bg-slate-800 overflow-hidden">
              <div className={`h-full ${pct >= minijob.warn_percent ? 'bg-amber-400' : 'bg-brand-500'}`} style={{ width: `${pct}%` }} />
            </div>
          )}
          {minijob.earnings_cents == null && (
            <div className="text-xs text-amber-300">Stundenlohn eintragen, damit der Verdienst berechnet werden kann.</div>
          )}
        </div>
      )}

      <div data-test-anchor="feste-pausen">
        <div className="text-xs text-slate-500 mb-1.5">Feste Pausen</div>
        <BreakModeSelect mode={breakMode} onChange={canWrite ? setBreakMode : undefined} tenantEnabled={tenantFixed.enabled} disabled={!canWrite} />
        {breakMode === 'standard' && tenantFixed.enabled && (
          <div className="mt-1.5 text-xs text-slate-400">Betriebsregel: {describeBreakWindows(tenantFixed.windows)} — abgezogen wird nur der Teil, in dem gearbeitet wurde.</div>
        )}
        {breakMode === 'standard' && !tenantFixed.enabled && (
          <div className="mt-1.5">
            <div className="text-xs text-slate-500 mb-1.5">Feste Pausen pro Wochentag (Minuten) — Mindest-Abzug an Stempel-Tagen</div>
            <MinutesRow values={breaks} onChange={canWrite ? setBreaks : undefined} disabled={!canWrite} />
          </div>
        )}
        {breakMode === 'eigene' && (
          <div className="mt-1.5">
            <div className="text-xs text-slate-500 mb-1.5">Eigene Pausenzeiten je Wochentag — gehen der Betriebsregel vor; abgezogen wird nur der anwesende Teil.</div>
            <BreakWindowsEditor rows={ownRows} onChange={canWrite ? setOwnRows : undefined} disabled={!canWrite} />
          </div>
        )}
        {breakMode === 'keine' && (
          <div className="mt-1.5 text-xs text-slate-400">Keine festen Pausen — nur die gesetzliche Mindestpause und gestempelte Pausen werden abgezogen.</div>
        )}
      </div>

      {/* AZM-H8: Arbeitswoche wird HIER gepflegt (Perioden-Split ab „Gilt ab") —
          das Feld im Stammdaten-Tab ist nur noch Anzeige. */}
      <div className="pt-2 border-t border-slate-800">
        <div className="flex items-baseline justify-between mb-1.5">
          <div className="text-xs text-slate-500">Arbeitswoche — Tages-Soll pro Wochentag (Minuten)</div>
        </div>
        <MinutesRow values={targets} onChange={canWrite ? setTargets : undefined} disabled={!canWrite} />
        {/* PT-2 (2026-08-28, Joerg): Wochensumme live anzeigen — Plantafel und
            Auswertungen rechnen mit dieser Summe, nicht mit dem statischen
            Stammdaten-Feld „Wochenstunden". */}
        <div className="mt-1 text-xs text-slate-500">
          Summe: {(() => { const h = targets.reduce((a, b) => a + (Number(b) || 0), 0) / 60; return Number.isInteger(h) ? String(h) : h.toLocaleString('de-DE', { maximumFractionDigits: 1 }); })()} h/Woche
        </div>
        {empType !== 'vollzeit' && zeitModus === 'feste_zeiten' && (
          <div className="mt-2">
            <div className="text-xs text-slate-500 mb-1">Feste Uhrzeiten je Wochentag (Von / Bis) — leer = an dem Tag keine</div>
            <div className="grid grid-cols-7 gap-1.5">
              {MZ_WEEKDAYS.map((wd, i) => (
                <div key={wd} className="text-center">
                  <span className="block text-[10px] uppercase text-slate-500">{wd}</span>
                  <input type="time" className={`${inp} px-1 text-center text-xs`} value={startTimes[i] ?? ''} disabled={!canWrite}
                         onChange={(e) => setStartTimes((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))} />
                  <input type="time" className={`${inp} px-1 text-center text-xs mt-1`} value={endTimes[i] ?? ''} disabled={!canWrite}
                         onChange={(e) => setEndTimes((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AZM-H8: Beschaeftigungs-Historie — jede Zeile ist eine Periode der
          lueckenlosen Zeitachse (Typ/Lohn/Grenze). Nur der juengste Wechsel
          laesst sich zuruecknehmen; aeltere korrigiert das Buero rueckwirkend
          ueber einen neuen Wechsel mit frueherem „Gilt ab". */}
      {empPeriods.length > 0 && (
        <div className="pt-2 border-t border-slate-800">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Beschäftigungs-Historie</div>
          <ul className="space-y-1.5">
            {empPeriods.map((p, idx) => (
              <li key={p.id} className="flex items-center justify-between gap-2 text-sm text-slate-200 rounded bg-slate-950/40 px-2.5 py-1.5">
                <div className="min-w-0">
                  <span className="font-medium">{MZ_EMPLOYMENT_LABEL_LEGACY[p.employment_type] ?? p.employment_type}</span>
                  <span className="text-slate-400"> · {fmtDate(p.valid_from)} – {p.valid_to ? fmtDate(p.valid_to) : 'offen'}</span>
                  {p.employment_type !== 'vollzeit' && p.zeit_modus && (
                    <span className="text-xs text-slate-500"> · {p.zeit_modus === 'feste_zeiten' ? 'feste Uhrzeiten' : 'Gleitzeit'}</span>
                  )}
                  {p.hourly_wage_cents != null && (
                    <span className="text-xs text-slate-500"> · {centsToEuroStr(p.hourly_wage_cents)} €/Std</span>
                  )}
                  {p.monthly_earnings_limit_cents != null && (
                    <span className="text-xs text-slate-500"> · Grenze {centsToEuroStr(p.monthly_earnings_limit_cents)} €</span>
                  )}
                </div>
                {canWrite && idx === 0 && empPeriods.length > 1 && (confirmEmpDelete === p.id ? (
                  <span className="flex items-center gap-1.5 shrink-0">
                    <button type="button" onClick={() => void deleteEmpPeriod(p.id)} disabled={busy}
                            className="text-xs px-2 py-1 rounded bg-rose-900/60 text-rose-200 hover:bg-rose-900 disabled:opacity-60">Wirklich zurücknehmen</button>
                    <button type="button" onClick={() => setConfirmEmpDelete(null)}
                            className="text-xs px-2 py-1 rounded text-slate-400 hover:bg-slate-800">Abbrechen</button>
                  </span>
                ) : (
                  <button type="button" onClick={() => setConfirmEmpDelete(p.id)}
                          className="text-xs px-2 py-1 rounded text-slate-400 hover:text-rose-300 hover:bg-rose-950/30 shrink-0">Zurücknehmen</button>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="pt-2 border-t border-slate-800">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-slate-500">Zeiträume (Basis + Abweichungen, z.B. Semesterferien)</div>
          {canWrite && !showPeriodForm && (
            <button type="button" onClick={() => setShowPeriodForm(true)}
                    className="text-xs px-2 py-1 rounded border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100">
              Zeitraum anlegen
            </button>
          )}
        </div>
        {periods.length === 0 && !showPeriodForm && (
          <div className="text-xs text-slate-500">Keine — es gilt durchgehend die Arbeitswoche oben.</div>
        )}
        <ul className="space-y-1.5">
          {periods.map((p) => {
            const isBasis = p.label.startsWith('Basisprofil') || p.label.startsWith('Arbeitszeiten ab ');
            return (
            <li key={p.id} className="flex items-center justify-between gap-2 text-sm text-slate-200 rounded bg-slate-950/40 px-2.5 py-1.5">
              <div className="min-w-0">
                <span className="font-medium">{p.label}</span>
                {isBasis && (
                  <span className="ml-1.5 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 align-middle"
                        title="Automatisch angelegt (aus den Stammdaten bzw. der Arbeitswoche-Bearbeitung). Gilt als normales Modell — bei überlappenden Zeiträumen gewinnt der mit dem späteren Beginn.">Basis</span>
                )}
                <span className="text-slate-400"> · {fmtDate(p.valid_from)} – {p.valid_to ? fmtDate(p.valid_to) : 'offen'}</span>
                <span className="text-xs text-slate-500"> · Woche {Math.round(p.daily_target_minutes.reduce((s, n) => s + n, 0) / 60 * 10) / 10} Std</span>
              </div>
              {canWrite && (confirmDelete === p.id ? (
                <span className="flex items-center gap-1.5 shrink-0">
                  <button type="button" onClick={() => void deletePeriod(p.id)} disabled={busy}
                          title={isBasis ? 'Wirkt rückwirkend: Tage ohne Zeitraum fallen auf die aktuelle Arbeitswoche zurück.' : undefined}
                          className="text-xs px-2 py-1 rounded bg-rose-900/60 text-rose-200 hover:bg-rose-900 disabled:opacity-60">{isBasis ? 'Wirklich löschen (rückwirkend)' : 'Wirklich löschen'}</button>
                  <button type="button" onClick={() => setConfirmDelete(null)}
                          className="text-xs px-2 py-1 rounded text-slate-400 hover:bg-slate-800">Abbrechen</button>
                </span>
              ) : (
                <span className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => startEditPeriod(p)}
                          title="Zeitraum nachträglich ändern — betroffene Tage werden neu berechnet (auch rückwirkend)."
                          className="text-xs px-2 py-1 rounded text-slate-400 hover:text-slate-100 hover:bg-slate-800">Bearbeiten</button>
                  <button type="button" onClick={() => setConfirmDelete(p.id)}
                          className="text-xs px-2 py-1 rounded text-slate-400 hover:text-rose-300 hover:bg-rose-950/30">Löschen</button>
                </span>
              ))}
            </li>
            );
          })}
        </ul>
        {showPeriodForm && (
          <div className="mt-2 rounded border border-slate-800 bg-slate-950/50 p-3 space-y-2.5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <div className="text-xs text-slate-500 mb-1">Bezeichnung</div>
                <input type="text" className={inp} value={pLabel} placeholder="z.B. Semesterferien Ostern"
                       onChange={(e) => setPLabel(e.target.value)} />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Von</div>
                <input type="date" className={inp} value={pFrom} onChange={(e) => setPFrom(e.target.value)} />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Bis (leer = offen)</div>
                <input type="date" className={inp} value={pTo} onChange={(e) => setPTo(e.target.value)} />
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Tages-Soll (Minuten)</div>
              <MinutesRow values={pTargets} onChange={setPTargets} />
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Feste Pausen</div>
              <BreakModeSelect mode={pBreakMode} onChange={setPBreakMode} tenantEnabled={tenantFixed.enabled} />
              {pBreakMode === 'standard' && !tenantFixed.enabled && (
                <div className="mt-1.5">
                  <div className="text-xs text-slate-500 mb-1">Feste Pausen (Minuten)</div>
                  <MinutesRow values={pBreaks} onChange={setPBreaks} />
                </div>
              )}
              {pBreakMode === 'standard' && tenantFixed.enabled && (
                <div className="mt-1.5 text-xs text-slate-400">Betriebsregel: {describeBreakWindows(tenantFixed.windows)}</div>
              )}
              {pBreakMode === 'eigene' && <div className="mt-1.5"><BreakWindowsEditor rows={pOwnRows} onChange={setPOwnRows} /></div>}
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-500">{editPeriodId ? 'Änderung' : 'Neuer Zeitraum'} wird mit „Speichern“ oben übernommen.</span>
              <button type="button" onClick={closePeriodForm}
                      className="text-xs px-2.5 py-1.5 rounded text-slate-300 hover:bg-slate-800">Abbrechen</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
interface CorrectionEntry { id: string; original_stamp_id: string | null; requested_change: Record<string, unknown>; reason: string | null; status: string; decided_at: string | null; decision_comment: string | null; created_at: string }
interface BerichtRow { id: string; task_id: string | null; title: string; status: string; created_at: string }
interface AufgabeRow { id: string; title: string; status: string; priority: string | null; due_date: string | null; customer_id: string | null; created_at: string; rolle: 'owner' | 'helper' }

function AkteTab({ antraege, userId, personId, audit, canWriteAkte, editing, form, setField, person, registry }: {
  antraege: AntragRow[]; userId: string; personId: string | null; audit: AuditEntry[];
  canWriteAkte: boolean;
  /** BA: Sammelstelle des globalen Speichern-Knopfs (Arbeitszeitmodell-Karte meldet sich an). */
  registry?: SaveRegistry;
  // Joerg 2026-08-09 (Zeitkonto-Sprint): Personen-Bearbeitung wird von der
  // aeusseren Komponente gesteuert (Save-Button oben in Topbar). Zeiterfassungs-
  // Sub-Tab nutzt die selben form-State + setField, damit hire_date/exit_date/
  // start_saldo_minutes im gemeinsamen Save-Handler landen.
  editing: boolean;
  form: Partial<PersonDetail>;
  setField: (k: keyof PersonDetail, v: unknown) => void;
  person: PersonDetail;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Cluster: Controlling-Sub-Tab nur fuer GF/Admin (Joerg 2026-07-04).
  const { user, hasPermission } = useAuth();
  const canControlling = user?.role === 'admin' || user?.role === 'gf' || hasPermission('zeiterfassung.approve');
  const sub = (searchParams.get('sub') as SubTabKey | null) ?? 'antraege';
  // Ersetzend (R2): Reiterwechsel erzeugen keinen Verlaufseintrag.
  const setSub = (k: SubTabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('sub', k);
    setSearchParams(next, { replace: true, preventScrollReset: true });
  };
  const [arbeitSearch, setArbeitSearch] = useState('');
  const [salden, setSalden] = useState<SaldoEntry[]>([]);
  const [balance, setBalance] = useState<BalanceSummary | null>(null);
  const [summary, setSummary] = useState<TimekeepingSummary | null>(null);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeMsg, setRecomputeMsg] = useState<string | null>(null);
  // Stempel-Liste kommt jetzt aus der Federation-Komponente UserMonthDetail
  // (mit Monatswechsler + Chips). Der lokale /admin/stamps/by-user-Fetch
  // ist damit tot — Joerg 2026-08-08.
  const [corrections, setCorrections] = useState<CorrectionEntry[]>([]);
  const [berichte, setBerichte] = useState<BerichtRow[]>([]);
  const [aufgaben, setAufgaben] = useState<AufgabeRow[]>([]);
  const [ztLoading, setZtLoading] = useState(false);
  const [arbeitLoading, setArbeitLoading] = useState(false);
  const [yearFilter, setYearFilter] = useState<string>('current');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (sub !== 'zeiterfassung' || !userId) return;
    setZtLoading(true);
    Promise.all([
      api.get<{ success: boolean; data: BalanceSummary }>(`/modules/zeiterfassung/admin/balance-summary/${userId}`).then((r) => setBalance(r.data)).catch(() => setBalance(null)),
      api.get<{ success: boolean; data: SaldoEntry[] }>(`/modules/zeiterfassung/admin/saldo-adjustments/${userId}`).then((r) => setSalden(r.data ?? [])).catch(() => setSalden([])),
      api.get<{ success: boolean; data: CorrectionEntry[] }>(`/modules/zeiterfassung/admin/corrections/by-user/${userId}`).then((r) => setCorrections(r.data ?? [])).catch(() => setCorrections([])),
      api.get<{ success: boolean; data: TimekeepingSummary }>(`/modules/zeiterfassung/admin/timekeeping-summary/${userId}`).then((r) => setSummary(r.data)).catch(() => setSummary(null)),
    ]).finally(() => setZtLoading(false));
  }, [sub, userId]);

  useEffect(() => {
    if (sub !== 'arbeit' || !userId) return;
    setArbeitLoading(true);
    Promise.all([
      api.get<{ success: boolean; data: BerichtRow[] }>(`/modules/bauberichteV2/admin/reports/by-user/${userId}`).then((r) => setBerichte(r.data ?? [])).catch(() => setBerichte([])),
      api.get<{ success: boolean; data: AufgabeRow[] }>(`/modules/bauberichteV2/admin/tasks/by-user/${userId}`).then((r) => setAufgaben(r.data ?? [])).catch(() => setAufgaben([])),
    ]).finally(() => setArbeitLoading(false));
  }, [sub, userId]);

  // Antraege filtern
  const filteredAntraege = useMemo(() => {
    const cy = new Date().getFullYear();
    return antraege.filter((a) => {
      if (yearFilter === 'current' && new Date(a.from_date).getFullYear() !== cy) return false;
      if (yearFilter !== 'current' && yearFilter !== 'all' && String(new Date(a.from_date).getFullYear()) !== yearFilter) return false;
      if (typeFilter !== 'all' && a.type !== typeFilter) return false;
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      return true;
    });
  }, [antraege, yearFilter, typeFilter, statusFilter]);

  // Resturlaubs-Konto fuer aktuelles Jahr aus genehmigten Urlaubs-Antraegen
  const restUrlaub = useMemo(() => {
    const cy = new Date().getFullYear();
    const yearGenehmigt = antraege.filter((a) =>
      a.type === 'urlaub' && a.status === 'genehmigt' &&
      new Date(a.from_date).getFullYear() === cy,
    );
    const verbraucht = yearGenehmigt.reduce((s, a) => s + (a.aufschluesselung?.netto ?? 0), 0);
    return { verbraucht };
  }, [antraege]);

  // Salden-Aggregat
  const saldenSum = useMemo(
    () => salden.reduce((s, e) => s + (e.minutes ?? 0), 0),
    [salden],
  );

  return (
    <div className="space-y-3">
      <nav className="flex gap-4 border-b border-slate-800 text-sm">
        {(['antraege', 'zeiterfassung', 'arbeit', 'aktestruktur', 'dokumente', 'zertifikate', 'controlling', 'verlauf'] as const)
          .filter((k) => k !== 'controlling' || canControlling)
          .map((k) => (
          <button key={k} onClick={() => setSub(k)}
                  className={`pb-2 -mb-px border-b-2 transition ${
                    sub === k ? 'border-brand-500 text-brand-300 font-medium' : 'border-transparent text-slate-400 hover:text-slate-200'
                  }`}>
            {k === 'antraege' ? `Anträge (${antraege.length})`
             : k === 'zeiterfassung' ? 'Zeiterfassung'
             : k === 'arbeit' ? 'Arbeit'
             : k === 'aktestruktur' ? 'Akte-Struktur'
             : k === 'dokumente' ? 'Dokumente'
             : k === 'zertifikate' ? 'Zertifikate'
             : k === 'controlling' ? 'Controlling'
             : 'Verlauf'}
          </button>
        ))}
      </nav>

      {sub === 'antraege' && (
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Resturlaubs-Konto {new Date().getFullYear()}</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-slate-500">Verbraucht (netto)</div>
                <div className="text-2xl font-semibold text-slate-100">{fmtTage(restUrlaub.verbraucht)} <span className="text-sm text-slate-400 font-normal">Tage</span></div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Anspruch (im Stammdaten-Tab)</div>
                <div className="text-2xl font-semibold text-slate-300">siehe Konditionen</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Hinweis</div>
                <div className="text-xs text-slate-400 mt-1">§9 BUrlG: Krank-im-Urlaub-Tage werden zurückgegeben (siehe Spalten −Krank / Netto unten).</div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <div className="flex items-end gap-3 mb-3">
              <Field label="Jahr">
                <select className={inp} value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
                  <option value="current">{new Date().getFullYear()} (aktuell)</option>
                  <option value={String(new Date().getFullYear() - 1)}>{new Date().getFullYear() - 1}</option>
                  <option value="all">Alle</option>
                </select>
              </Field>
              <Field label="Typ">
                <select className={inp} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  <option value="all">Alle</option>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select className={inp} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">Alle</option>
                  <option value="beantragt">beantragt</option>
                  <option value="genehmigt">genehmigt</option>
                  <option value="abgelehnt">abgelehnt</option>
                  <option value="storniert">storniert</option>
                </select>
              </Field>
            </div>
            {filteredAntraege.length === 0
              ? <div className="text-center text-slate-500 py-4">Keine Anträge im Filter.</div>
              : (
                <div className="rounded border border-slate-800 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800/50 text-slate-400 text-xs uppercase">
                      <tr>
                        <th className="text-left  px-3 py-2 font-medium">Typ</th>
                        <th className="text-left  px-3 py-2 font-medium">Zeitraum</th>
                        <th className="text-right px-3 py-2 font-medium">Brutto</th>
                        <th className="text-right px-3 py-2 font-medium" title="Feiertage">−Feiertage</th>
                        <th className="text-right px-3 py-2 font-medium" title="§9 BUrlG">−Krank</th>
                        <th className="text-right px-3 py-2 font-medium">Netto</th>
                        <th className="text-left  px-3 py-2 font-medium">Status</th>
                        <th className="text-left  px-3 py-2 font-medium">Genehmigt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAntraege.map((a) => {
                        const auf = a.aufschluesselung;
                        // Joerg 2026-07-04: Warnung wenn MA im Urlaub gestempelt hat.
                        // Portal macht NICHTS automatisch — GF entscheidet ob
                        // Saldo angepasst wird (via saldo_adjustments urlaubsrueckgabe).
                        const showOverlap = a.type === 'urlaub' && (a.overlap_stamps_days ?? 0) > 0;
                        const overlapMin = a.overlap_stamps_minutes ?? 0;
                        const overlapH = Math.floor(overlapMin / 60);
                        const overlapM = overlapMin % 60;
                        return (
                          <Fragment key={a.id}>
                            <tr className="border-t border-slate-800">
                              <td className="px-3 py-2 text-slate-100">{TYPE_LABEL[a.type] ?? a.type}</td>
                              <td className="px-3 py-2 text-slate-300">
                                {/* Joerg 2026-09-04: Zeitraum verlinkt auf die Antrags-
                                    Detailseite (Gutschrift/Teilstorno liegt nur dort). */}
                                <Link to={`/m/zeiterfassung/antraege/${a.id}`}
                                      className="hover:text-brand-300 hover:underline"
                                      title="Antrag öffnen (Details / Gutschrift)">
                                  {a.from_date === a.to_date
                                    ? (a.duration_minutes != null
                                        ? `${fmtDate(a.from_date)} (${fmtStd(a.duration_minutes)} Std)`
                                        : a.half_day_from || a.half_day_to ? `${fmtDate(a.from_date)} (½)` : fmtDate(a.from_date))
                                    : `${fmtDate(a.from_date)} – ${fmtDate(a.to_date)}`}
                                </Link>
                              </td>
                              {/* ABW 2026-08-28: Stunden-ZA zaehlt nicht als voller Tag —
                                  Brutto/Netto in Stunden statt "1 Tag" ausweisen. */}
                              <td className="px-3 py-2 text-right tabular-nums text-slate-200">{a.duration_minutes != null ? `${fmtStd(a.duration_minutes)} Std` : fmtTage(auf.brutto)}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-slate-400">{auf.feiertage > 0 ? `−${fmtTage(auf.feiertage)}` : '–'}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-slate-400">{auf.krank > 0 ? `−${fmtTage(auf.krank)}` : '–'}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-slate-100 font-semibold">{a.duration_minutes != null ? `${fmtStd(a.duration_minutes)} Std` : fmtTage(auf.netto)}</td>
                              <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded ${STATUS_CLASS[a.status] ?? 'bg-slate-700 text-slate-300'}`}>{a.status}</span></td>
                              <td className="px-3 py-2 text-xs text-slate-500">
                                {a.approved_at ? `${fmtDate(a.approved_at)} — ${a.approver_first_name ?? ''} ${a.approver_last_name ?? ''}` : '–'}
                              </td>
                            </tr>
                            {showOverlap && (
                              <tr className="bg-amber-900/10 border-t border-amber-900/40">
                                <td colSpan={8} className="px-3 py-2 text-xs text-amber-200">
                                  ⚠ Im Urlaub wurde an {a.overlap_stamps_days} Tag{a.overlap_stamps_days === 1 ? '' : 'en'} gestempelt
                                  {overlapMin > 0 && ` (${overlapH} h${overlapM > 0 ? ` ${overlapM} min` : ''} tatsächliche Arbeit)`}.
                                  {' '}Portal passt den Saldo <b>nicht automatisch</b> an — GF-Entscheidung, ggf. via Saldo-Bewegung (kind=urlaubsrueckgabe).
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      )}

      {sub === 'zeiterfassung' && (
        <div className="space-y-3">
          {/* Joerg 2026-08-09: Zeitkonto-Konfiguration — Beschaeftigungs-
              Zeitraum, Uebernahme-Saldo aus Altsystem, manueller Recompute.
              Aenderungen an hire/exit_date/target loesen im Backend automatisch
              recomputeRange aus (PUT /admin/employee-settings). Der Button
              "Neu berechnen" ist Sicherheitsnetz falls Cache mal verrutscht. */}
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Zeitkonto-Konfiguration</h3>
              {canWriteAkte && !editing && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!userId) return;
                    setRecomputing(true); setRecomputeMsg(null);
                    try {
                      const r = await fetch(`/api/modules/zeiterfassung/admin/recompute-balances`, {
                        method: 'POST', credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId }),
                      });
                      if (!r.ok) {
                        const err = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
                        throw new Error((err as { error?: string }).error ?? r.statusText);
                      }
                      const j = await r.json() as { data?: { total_days?: number } };
                      setRecomputeMsg(`Neu berechnet: ${j.data?.total_days ?? 0} Tage.`);
                      // Summary neu laden — total_minutes koennte sich aendern
                      api.get<{ success: boolean; data: TimekeepingSummary }>(`/modules/zeiterfassung/admin/timekeeping-summary/${userId}`).then((rr) => setSummary(rr.data)).catch(() => {});
                      api.get<{ success: boolean; data: BalanceSummary }>(`/modules/zeiterfassung/admin/balance-summary/${userId}`).then((rr) => setBalance(rr.data)).catch(() => {});
                    } catch (e) {
                      setRecomputeMsg(`Fehler: ${(e as Error).message}`);
                    } finally {
                      setRecomputing(false);
                    }
                  }}
                  disabled={recomputing}
                  className="text-xs px-2 py-1 rounded border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100 disabled:opacity-40"
                  title="Rechnet daily_balances fuer diesen Mitarbeiter neu aus stamps + absences. Nutzt der Backend-Handler ohnehin bei jeder Aenderung — Button ist Sicherheitsnetz."
                >
                  {recomputing ? 'Berechne…' : 'Zeitkonto neu berechnen'}
                </button>
              )}
            </div>
            {recomputeMsg && (
              <div className={`text-xs mb-2 ${recomputeMsg.startsWith('Fehler') ? 'text-rose-300' : 'text-emerald-300'}`}>{recomputeMsg}</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-slate-500 mb-1">Zeiterfassung aktiv seit</div>
                {editing
                  ? <input type="date" className={inp} value={(form.hire_date ?? '').slice(0, 10)} onChange={(e) => setField('hire_date', e.target.value || null)} />
                  : <div className="text-sm text-slate-100">{person.hire_date ? fmtDate(person.hire_date) : <span className="text-slate-500">–</span>}</div>}
                <div className="text-xs text-slate-500 mt-1">Ab diesem Tag wird Soll/Netto berechnet. Davor: Saldo = 0.</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Austritt</div>
                {editing
                  ? <input type="date" className={inp} value={(form.exit_date ?? '').slice(0, 10)} onChange={(e) => setField('exit_date', e.target.value || null)} />
                  : <div className="text-sm text-slate-100">{person.exit_date ? fmtDate(person.exit_date) : <span className="text-slate-500">–</span>}</div>}
                <div className="text-xs text-slate-500 mt-1">Optional. Nach diesem Tag wird kein Soll mehr berechnet.</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Uebernommener Saldo (aus Altsystem)</div>
                {editing ? (
                  <>
                    <input
                      type="number"
                      step="1"
                      className={inp}
                      value={form.start_saldo_minutes ?? 0}
                      onChange={(e) => setField('start_saldo_minutes', e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Minuten. Anzeige: <span className="font-mono text-slate-300">{fmtMinutes(Number(form.start_saldo_minutes ?? 0))}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`text-sm font-semibold ${(person.start_saldo_minutes ?? 0) >= 0 ? 'text-brand-300' : 'text-rose-300'}`}>
                      {fmtMinutes(person.start_saldo_minutes ?? 0)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Positiv = Guthaben, negativ = Rueckstand.</div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* MZ 2026-08-27: Arbeitszeitmodell — Beschaeftigungsart, feste
              Pausen, abweichende Soll-Zeitraeume, Minijob-Monatsstand.
              Speichert direkt ueber die Modul-API (approve-Recht).
              BA (Joerg 2026-08-28): editierbar NUR im Bearbeiten-Modus der
              Akte — wie die Zeitkonto-Konfiguration darueber. */}
          {userId && (
            <ArbeitszeitmodellCard
              userId={userId}
              canWrite={canWriteAkte && editing}
              showEditHint={canWriteAkte && !editing}
              registry={registry}
              minijob={summary?.minijob ?? null}
              onChanged={() => {
                api.get<{ success: boolean; data: TimekeepingSummary }>(`/modules/zeiterfassung/admin/timekeeping-summary/${userId}`).then((rr) => setSummary(rr.data)).catch(() => {});
                api.get<{ success: boolean; data: BalanceSummary }>(`/modules/zeiterfassung/admin/balance-summary/${userId}`).then((rr) => setBalance(rr.data)).catch(() => {});
                // 1.29.0: Wochenstunden der Person = Summe des heute gültigen Modells (Leser:
                // Plantafel, Auswertungen, Mitarbeiterliste) — das Stammdaten-Feld ist nur Anzeige.
                api.get<{ success: boolean; data: MzEmploymentData }>(`/modules/zeiterfassung/admin/employment/${userId}`).then((rr) => {
                  const dtm = rr.data?.daily_target_minutes ?? null;
                  if (!dtm || dtm.length === 0) return;
                  const hours = Math.round(dtm.reduce((a, b) => a + (Number(b) || 0), 0) / 60);
                  if (person.weekly_hours !== hours) void api.put(`/admin/adressbuch/persons/${personId}`, { weeklyHours: hours }).catch(() => {});
                }).catch(() => {});
              }}
            />
          )}

          {/* Joerg 2026-08-10: Urlaubskonto direkt im Zeitkonto-Sub-Tab —
              vorher nur unter Stammdaten→Konditionen editierbar und
              Resturlaub nur im Antraege-Sub-Tab sichtbar. Jetzt beides
              zusammen: Uebersicht aus summary.leave.per_year + Editor inline
              (schreibt in form.leave_quotas, wird beim gemeinsamen Save wie
              bisher via /admin/leave-quotas/:userId synchronisiert). */}
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Urlaubskonto</h3>
              {summary && (
                <div className="text-xs text-slate-400">
                  Resturlaub gesamt:{' '}
                  <span className={`font-semibold ${summary.leave.remaining_total_days >= 0 ? 'text-brand-300' : 'text-rose-300'}`}>
                    {summary.leave.remaining_total_days.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 1 })} Tage
                  </span>
                </div>
              )}
            </div>
            {(() => {
              // Merge: Basis ist per_year aus summary (fuer used/remaining),
              // Overlay aus form.leave_quotas (fuer Edit-Werte).
              const perYear = summary?.leave.per_year ?? [];
              type Q = { year: number; type: string; total_days: number; carryover_days: number };
              const formQuotas: Q[] = ((editing ? form.leave_quotas : person.leave_quotas) ?? []).filter((q) => q.type === 'urlaub');
              // Alle Jahre die entweder in summary oder in form.leave_quotas vorkommen
              const years = new Set<number>();
              perYear.forEach((r) => years.add(r.year));
              formQuotas.forEach((q) => years.add(q.year));
              const rows = Array.from(years).sort((a, b) => b - a).map((year) => {
                const s = perYear.find((r) => r.year === year);
                const q = formQuotas.find((qq) => qq.year === year);
                return {
                  year,
                  total: Number(q?.total_days ?? s?.total_days ?? 0),
                  carry: Number(q?.carryover_days ?? s?.carryover_days ?? 0),
                  used: s?.used_days ?? 0,
                  remaining: s?.remaining_days ?? 0,
                };
              });
              const setQuotaLocal = (year: number, patch: { total?: number; carry?: number }) => {
                const list: Q[] = (form.leave_quotas ?? person.leave_quotas ?? []).map((qq) => ({ ...qq }));
                const idx = list.findIndex((qq) => qq.year === year && qq.type === 'urlaub');
                if (idx >= 0) {
                  if (patch.total !== undefined) list[idx]!.total_days = patch.total;
                  if (patch.carry !== undefined) list[idx]!.carryover_days = patch.carry;
                } else {
                  list.push({ year, type: 'urlaub', total_days: patch.total ?? 0, carryover_days: patch.carry ?? 0 });
                }
                setField('leave_quotas', list);
              };
              const addYearLocal = () => {
                const list: Q[] = (form.leave_quotas ?? person.leave_quotas ?? []).map((qq) => ({ ...qq }));
                const usedYears = new Set(list.filter((qq) => qq.type === 'urlaub').map((qq) => qq.year));
                let target = new Date().getFullYear();
                while (usedYears.has(target)) target++;
                list.push({ year: target, type: 'urlaub', total_days: 0, carryover_days: 0 });
                setField('leave_quotas', list);
              };
              if (rows.length === 0 && !editing) {
                return <div className="text-sm text-slate-500">Keine Urlaubs-Eintraege. Zum Anlegen: „Bearbeiten" oben.</div>;
              }
              return (
                <>
                  {/* MZ-7: Teilzeit-Anspruch-Hinweis + Umrechner */}
                  <TeilzeitAnspruchHinweis
                    dtm={(editing ? form.daily_target_minutes : undefined) ?? person.daily_target_minutes}
                    canConvert={editing && canWriteAkte}
                    onApply={(days) => setQuotaLocal(new Date().getFullYear(), { total: days })}
                  />
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500">
                      <tr>
                        <th className="text-left py-1">Jahr</th>
                        <th className="text-right py-1">Anspruch</th>
                        <th className="text-right py-1">Uebertrag</th>
                        <th className="text-right py-1">Gesamt</th>
                        <th className="text-right py-1">Verbraucht</th>
                        <th className="text-right py-1">Rest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.year} className="border-t border-slate-800">
                          <td className="py-1 text-slate-200">{r.year}</td>
                          <td className="py-1 text-right">
                            {editing && canWriteAkte
                              ? <input type="number" min={0} max={365} step={0.5} value={r.total}
                                  onChange={(e) => setQuotaLocal(r.year, { total: Number(e.target.value) })}
                                  className="w-20 h-7 px-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-right tabular-nums" />
                              : <span className="tabular-nums text-slate-100">{fmtTage(r.total)}</span>}
                          </td>
                          <td className="py-1 text-right">
                            {editing && canWriteAkte
                              ? <input type="number" min={0} max={365} step={0.5} value={r.carry}
                                  onChange={(e) => setQuotaLocal(r.year, { carry: Number(e.target.value) })}
                                  className="w-20 h-7 px-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-right tabular-nums" />
                              : <span className="tabular-nums text-slate-400">{fmtTage(r.carry)}</span>}
                          </td>
                          <td className="py-1 text-right tabular-nums text-slate-100 font-semibold">{fmtTage(r.total + r.carry)}</td>
                          <td className="py-1 text-right tabular-nums text-slate-400">{fmtTage(r.used)}</td>
                          <td className={`py-1 text-right tabular-nums font-semibold ${r.remaining >= 0 ? 'text-brand-300' : 'text-rose-300'}`}>{fmtTage(r.remaining)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {editing && canWriteAkte && (
                    <div className="mt-2 flex items-center justify-between">
                      <button type="button" onClick={addYearLocal}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-brand-600 hover:bg-brand-500 text-primary-foreground text-xs">
                        <Plus size={12} /> Jahr hinzufuegen
                      </button>
                      <span className="text-[11px] text-slate-500">Aenderungen werden mit „Speichern" oben persistiert.</span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Stunden-Konto</h3>
            {summary ? (
              <>
                {/* Neu 2026-08-09: Aufschluesselung Uebernahme + Netto seit hire_date = Gesamt.
                    Zeigt dem Admin was aus Altsystem-Uebernahme kommt und was seit
                    Einfuehrung neu erfasst wurde. */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-slate-500">Uebernahme (Altsystem)</div>
                    <div className={`text-lg font-semibold ${summary.saldo.start_saldo_minutes >= 0 ? 'text-slate-100' : 'text-rose-300'}`}>{fmtMinutes(summary.saldo.start_saldo_minutes)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Netto seit Beginn</div>
                    <div className={`text-lg font-semibold ${summary.saldo.balance_sum_minutes >= 0 ? 'text-slate-100' : 'text-rose-300'}`}>{fmtMinutes(summary.saldo.balance_sum_minutes)}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{summary.saldo.days_counted} Tage</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Gesamt-Saldo</div>
                    <div className={`text-2xl font-bold ${summary.saldo.total_minutes >= 0 ? 'text-brand-300' : 'text-rose-300'}`}>{fmtMinutes(summary.saldo.total_minutes)}</div>
                  </div>
                </div>
                <div className="border-t border-slate-800 pt-3" />
              </>
            ) : null}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-slate-500">Saldo (gesamt)</div>
                <div className={`text-2xl font-semibold ${(balance?.total_saldo ?? 0) >= 0 ? 'text-brand-300' : 'text-rose-300'}`}>{fmtMinutes(balance?.total_saldo ?? 0)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Diese Woche (netto)</div>
                <div className="text-2xl font-semibold text-slate-100">{fmtMinutes(balance?.week_netto ?? 0)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Dieser Monat (netto)</div>
                <div className="text-2xl font-semibold text-slate-100">{fmtMinutes(balance?.month_netto ?? 0)}</div>
              </div>
            </div>
            <div className="text-xs text-slate-500 mt-2">Letzter erfasster Tag: {balance?.last_day ? fmtDate(balance.last_day) : '–'}</div>
          </div>

          {/* Joerg 2026-08-08: „Letzte 20 Stempel" ersetzt durch das
              Zeitkonto-Monats-Detail mit Monatswechsler — Federation-Import
              aus dem Zeiterfassungs-Modul, identisch zur Standalone-Route
              /m/zeiterfassung/team-monat/:userId. Persona-Header ausgeblendet,
              weil die Akte darueber schon die Person zeigt. */}
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <Suspense fallback={
              <div className="text-sm text-slate-500 inline-flex items-center gap-2">
                <Loader2 className="animate-spin" size={14} /> Lädt Monatskonto…
              </div>
            }>
              <UserMonthDetail
                userId={userId}
                showPersonaHeader={false}
                showLegend={false}
              />
            </Suspense>
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Saldo-Bewegungen ({salden.length})</h3>
            {salden.length === 0
              ? <div className="text-sm text-slate-500">Keine Saldo-Buchungen.</div>
              : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 bg-slate-800/40">
                    <tr>
                      <th className="text-left py-2 px-3">Tag</th>
                      <th className="text-left py-2 px-3">Art</th>
                      <th className="text-right py-2 px-3">Stunden</th>
                      <th className="text-left py-2 px-3">Grund</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salden.map((e) => (
                      <tr key={e.id} className="border-t border-slate-800">
                        <td className="py-1 px-3 text-slate-300">{fmtDate(e.day)}</td>
                        <td className="py-1 px-3 text-slate-100">{SALDO_KIND_LABEL[e.kind] ?? e.kind}</td>
                        <td className="py-1 px-3 text-right tabular-nums text-slate-200">{e.minutes < 0 ? '−' : '+'}{fmtMinutes(Math.abs(e.minutes))}</td>
                        <td className="py-1 px-3 text-slate-300">{e.reason ?? '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Korrektur-Anträge ({corrections.length})</h3>
            {corrections.length === 0
              ? <div className="text-sm text-slate-500">Keine Korrektur-Anträge.</div>
              : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 bg-slate-800/40">
                    <tr>
                      <th className="text-left py-2 px-3">Erstellt</th>
                      <th className="text-left py-2 px-3">Grund</th>
                      <th className="text-left py-2 px-3">Status</th>
                      <th className="text-left py-2 px-3">Entschieden</th>
                    </tr>
                  </thead>
                  <tbody>
                    {corrections.map((c) => (
                      <tr key={c.id} className="border-t border-slate-800">
                        <td className="py-1 px-3 text-slate-300 whitespace-nowrap">{fmtDateTime(c.created_at)}</td>
                        <td className="py-1 px-3 text-slate-300 text-xs">{c.reason ?? '–'}</td>
                        <td className="py-1 px-3"><span className={`text-xs px-2 py-0.5 rounded ${STATUS_CLASS[c.status] ?? 'bg-slate-700 text-slate-300'}`}>{c.status}</span></td>
                        <td className="py-1 px-3 text-xs text-slate-500">{c.decided_at ? fmtDateTime(c.decided_at) : '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </div>
      )}

      {sub === 'arbeit' && (() => {
        const q = arbeitSearch.trim().toLowerCase();
        const filteredBerichte = q
          ? berichte.filter((b) => b.title.toLowerCase().includes(q))
          : berichte;
        const filteredAufgaben = q
          ? aufgaben.filter((a) => a.title.toLowerCase().includes(q))
          : aufgaben;
        return (
        <div className="space-y-3">
          <div className="flex">
            <input type="search" value={arbeitSearch} onChange={(e) => setArbeitSearch(e.target.value)}
                   placeholder="Berichte und Aufgaben durchsuchen…"
                   className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
          </div>
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Letzte Berichte ({filteredBerichte.length}{q && ` von ${berichte.length}`})</h3>
            {arbeitLoading
              ? <div className="text-sm text-slate-500">Lädt…</div>
              : filteredBerichte.length === 0
                ? <div className="text-sm text-slate-500">{q ? 'Keine Treffer.' : 'Keine Berichte.'}</div>
                : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500 bg-slate-800/40">
                      <tr>
                        <th className="text-left py-2 px-3">Erstellt</th>
                        <th className="text-left py-2 px-3">Titel</th>
                        <th className="text-left py-2 px-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBerichte.map((b) => (
                        <tr key={b.id} className="border-t border-slate-800 hover:bg-slate-800/30">
                          <td className="py-1 px-3 text-slate-300 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                          <td className="py-1 px-3 text-slate-100">
                            <Link to={`/m/bauberichteV2/reports/${b.id}`} className="hover:text-brand-300">{b.title}</Link>
                          </td>
                          <td className="py-1 px-3"><span className={`text-xs px-2 py-0.5 rounded ${STATUS_CLASS[b.status] ?? 'bg-slate-700 text-slate-300'}`}>{b.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Aufgaben ({filteredAufgaben.length}{q && ` von ${aufgaben.length}`})</h3>
            {arbeitLoading
              ? <div className="text-sm text-slate-500">Lädt…</div>
              : filteredAufgaben.length === 0
                ? <div className="text-sm text-slate-500">{q ? 'Keine Treffer.' : 'Keine Aufgaben.'}</div>
                : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500 bg-slate-800/40">
                      <tr>
                        <th className="text-left py-2 px-3">Erstellt</th>
                        <th className="text-left py-2 px-3">Titel</th>
                        <th className="text-left py-2 px-3">Rolle</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Faellig</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAufgaben.map((a) => (
                        <tr key={a.id} className="border-t border-slate-800 hover:bg-slate-800/30">
                          <td className="py-1 px-3 text-slate-300 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                          <td className="py-1 px-3 text-slate-100">
                            <Link to={`/m/bauberichteV2/tasks/${a.id}`} className="hover:text-brand-300">{a.title}</Link>
                          </td>
                          <td className="py-1 px-3 text-xs text-slate-400">{a.rolle === 'owner' ? 'Owner' : 'Helfer'}</td>
                          <td className="py-1 px-3"><span className={`text-xs px-2 py-0.5 rounded ${STATUS_CLASS[a.status] ?? 'bg-slate-700 text-slate-300'}`}>{a.status}</span></td>
                          <td className="py-1 px-3 text-xs text-slate-400">{a.due_date ? fmtDate(a.due_date) : '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
          </div>
        </div>
        );
      })()}

      {sub === 'aktestruktur' && personId && (
        <AkteStrukturViewer source={{ kind: 'person', personId }} configTyp="mitarbeiter" />
      )}

      {sub === 'dokumente' && personId && (
        <PersonDokumente personId={personId} />
      )}

      {sub === 'zertifikate' && personId && (
        <PersonZertifikate personId={personId} canWrite={canWriteAkte} />
      )}

      {sub === 'controlling' && personId && canControlling && (
        <PersonControlling personId={personId} />
      )}

      {sub === 'verlauf' && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
          <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-3">Voller Verlauf</h3>
          {audit.length === 0
            ? <div className="text-sm text-slate-500">Audit-Log lädt aus dem Sicherheits-Tab. Klick erst auf „Sicherheit", dann zurück.</div>
            : (
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase">
                  <tr>
                    <th className="text-left py-1 pr-3">Zeit</th>
                    <th className="text-left py-1 pr-3">Akteur</th>
                    <th className="text-left py-1">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((e) => (
                    <Fragment key={e.id}>
                      <tr className="border-t border-slate-800 align-top">
                        <td className="py-1 pr-3 text-slate-400 whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                        <td className="py-1 pr-3 text-slate-200 whitespace-nowrap">{e.actor_full_name}</td>
                        <td className="py-1 text-slate-300"><AuditActionCell entry={e} /></td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      )}
    </div>
  );
}

// =========================== Personal-Dokumente ===========================

interface PersonDocument {
  id: string; title: string; filename: string; mime_type: string;
  size_bytes: number; created_at: string;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Cluster 15.6 (Probst 2026-06-25): Zertifikate mit Ablaufdatum. Buero/GF pflegt.
// Warnung ab 30 Tage vor Ablauf (amber), abgelaufen = rose.
interface CertificateRow {
  id: string;
  person_id: string;
  name: string;
  issuer: string | null;
  issued_at: string | null;
  expires_at: string | null;
  document_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return null;
  return Math.floor((t - Date.now()) / (1000 * 60 * 60 * 24));
}

function PersonZertifikate({ personId, canWrite }: { personId: string; canWrite: boolean }) {
  const dlg = useDialogs();
  const [rows, setRows] = useState<CertificateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CertificateRow | null>(null);

  const load = useCallback(() => {
    setLoading(true); setError('');
    api.get<{ success: boolean; data: CertificateRow[] }>(`/admin/adressbuch/persons/${personId}/certificates`)
      .then((r) => setRows(r.data ?? []))
      .catch((e: unknown) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [personId]);
  useEffect(() => { load(); }, [load]);

  async function remove(certId: string, name: string) {
    if (!(await dlg.confirm({ title: 'Zertifikat löschen?', message: `„${name}" wird endgültig entfernt.`, confirmLabel: 'Löschen', danger: true }))) return;
    try {
      await api.delete(`/admin/adressbuch/persons/${personId}/certificates/${certId}`);
      load();
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Zertifikate ({rows.length})</h3>
        {canWrite && (
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm"
          >
            + Zertifikat
          </button>
        )}
      </div>

      {error && <div className="text-sm text-rose-400">{error}</div>}
      {loading && <div className="text-sm text-slate-500">Lädt...</div>}
      {!loading && rows.length === 0 && !showForm && (
        <p className="text-sm text-slate-500 italic">Keine Zertifikate hinterlegt.</p>
      )}
      {!loading && rows.length > 0 && (
        <ul className="divide-y divide-slate-800/60 rounded-lg border border-slate-800 overflow-hidden">
          {rows.map((c) => {
            const days = daysUntil(c.expires_at);
            const isExpired = days !== null && days < 0;
            const isSoon = days !== null && days >= 0 && days <= 30;
            const badgeCls = isExpired
              ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
              : isSoon
                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                : c.expires_at
                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                  : 'bg-slate-700 text-slate-400 border border-slate-600';
            const badgeText = isExpired
              ? `Abgelaufen seit ${Math.abs(days!)} T`
              : isSoon
                ? `Läuft in ${days} T ab`
                : c.expires_at
                  ? `Gültig bis ${new Date(c.expires_at).toLocaleDateString('de-DE')}`
                  : 'Ohne Ablauf';
            return (
              <li key={c.id} className="px-3 py-2.5 flex items-start gap-3 hover:bg-slate-800/30">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-100 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-3">
                    {c.issuer && <span>Aussteller: {c.issuer}</span>}
                    {c.issued_at && <span>Ausgestellt: {new Date(c.issued_at).toLocaleDateString('de-DE')}</span>}
                  </div>
                  {c.notes && <div className="text-xs text-slate-400 mt-1 truncate">{c.notes}</div>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${badgeCls}`}>{badgeText}</span>
                  {canWrite && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => { setEditing(c); setShowForm(true); }}
                        className="text-[11px] text-slate-400 hover:text-brand-300 px-2 py-0.5 rounded hover:bg-slate-800/50"
                      >Bearbeiten</button>
                      <button
                        onClick={() => remove(c.id, c.name)}
                        className="text-[11px] text-slate-400 hover:text-rose-300 px-2 py-0.5 rounded hover:bg-slate-800/50"
                      >Löschen</button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showForm && (
        <CertificateForm
          personId={personId}
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CertificateForm({ personId, initial, onClose, onSaved }: {
  personId: string; initial: CertificateRow | null;
  onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [issuer, setIssuer] = useState(initial?.issuer ?? '');
  const [issuedAt, setIssuedAt] = useState(initial?.issued_at ?? '');
  const [expiresAt, setExpiresAt] = useState(initial?.expires_at ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function submit() {
    if (!name.trim()) { setError('Name des Zertifikats erforderlich.'); return; }
    setSubmitting(true); setError('');
    try {
      const body = {
        name: name.trim(),
        issuer: issuer.trim() || null,
        issued_at: issuedAt || null,
        expires_at: expiresAt || null,
        notes: notes.trim() || null,
      };
      if (initial) {
        await api.patch(`/admin/adressbuch/persons/${personId}/certificates/${initial.id}`, body);
      } else {
        await api.post(`/admin/adressbuch/persons/${personId}/certificates`, body);
      }
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="rounded-lg border border-brand-600/40 bg-slate-950/60 p-3 space-y-2">
      <div className="text-sm font-semibold text-brand-300">
        {initial ? 'Zertifikat bearbeiten' : 'Neues Zertifikat'}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="sm:col-span-2">
          <label className="text-xs text-slate-400 mb-1 block">Bezeichnung *</label>
          <input
            autoFocus type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Sachkundenachweis Gerüstbau"
            className="w-full h-9 px-3 rounded-md border border-slate-700 bg-slate-900 text-slate-100 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Aussteller</label>
          <input
            type="text" value={issuer} onChange={(e) => setIssuer(e.target.value)}
            placeholder="Institution oder Firma"
            className="w-full h-9 px-3 rounded-md border border-slate-700 bg-slate-900 text-slate-100 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Ausgestellt</label>
          <input
            type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)}
            className="w-full h-9 px-3 rounded-md border border-slate-700 bg-slate-900 text-slate-100 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Gültig bis</label>
          <input
            type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full h-9 px-3 rounded-md border border-slate-700 bg-slate-900 text-slate-100 text-sm"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-slate-400 mb-1 block">Notiz</label>
          <textarea
            value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            className="w-full px-3 py-2 rounded-md border border-slate-700 bg-slate-900 text-slate-100 text-sm"
          />
        </div>
      </div>
      {error && <div className="text-xs text-rose-400">{error}</div>}
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className="text-xs text-slate-400 hover:text-slate-100 px-3 py-1.5">
          Abbrechen
        </button>
        <button
          onClick={submit} disabled={submitting}
          className="text-xs px-4 py-1.5 rounded-md bg-brand-600 hover:bg-brand-500 text-primary-foreground disabled:opacity-50"
        >
          {submitting ? 'Speichere…' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}

// Controlling-Sub-Tab (Joerg 2026-07-04): pro-MA Auswertungen fuer GF/Admin.
// Kranktage aktuelles + Vorjahr, Ueberstunden-Trend 12 Monate, Urlaubsverbrauch.

interface ControllingData {
  year: number;
  prevYear: number;
  sickness: {
    currentYearDays: number;
    currentYearEpisodes: number;
    prevYearDays: number;
    prevYearEpisodes: number;
    trend: 'up' | 'down' | 'same';
  };
  overtime: {
    totalSaldoMinutes: number;
    monthlySaldos: Array<{ ym: string; saldoMinutes: number }>;
  };
  leave: {
    current: { totalDays: number | null; carryoverDays: number | null; usedDays: number };
    previous: { totalDays: number | null; carryoverDays: number | null; usedDays: number };
  };
}

function fmtHours(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${h}h ${String(m).padStart(2, '0')}m`;
}

function PersonControlling({ personId }: { personId: string }) {
  const [year, setYear] = useState<number>(() => new Date().getFullYear());
  const [data, setData] = useState<ControllingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    api.get<{ success: boolean; data: ControllingData }>(
      `/admin/adressbuch/persons/${personId}/controlling?year=${year}`
    )
      .then((r) => setData(r.data))
      .catch((e: unknown) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [personId, year]);

  const maxAbsSaldo = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.overtime.monthlySaldos.map((m) => Math.abs(m.saldoMinutes)));
  }, [data]);

  if (loading) return <div className="text-sm text-slate-500">Lädt Controlling-Daten…</div>;
  if (error) return <div className="text-sm text-rose-400">{error}</div>;
  if (!data) return null;

  const s = data.sickness;
  const trendIcon = s.trend === 'up' ? '↑' : s.trend === 'down' ? '↓' : '→';
  const trendCls = s.trend === 'up' ? 'text-rose-300' : s.trend === 'down' ? 'text-emerald-300' : 'text-slate-400';

  const l = data.leave;
  const currBudget = (l.current.totalDays ?? 0) + (l.current.carryoverDays ?? 0);
  const currRemaining = currBudget - l.current.usedDays;
  const prevBudget = (l.previous.totalDays ?? 0) + (l.previous.carryoverDays ?? 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Controlling — {year}</h3>
        <div className="flex gap-1">
          {[year - 2, year - 1, year, year + 1].map((y) => (
            <button key={y} onClick={() => setYear(y)}
              className={`px-2 py-1 text-xs rounded ${
                y === year ? 'bg-brand-600 text-primary-foreground' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}>{y}</button>
          ))}
        </div>
      </div>

      {/* Kranktage */}
      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Kranktage</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-slate-500">{year}</div>
            <div className="text-2xl font-semibold text-slate-100 tabular-nums">{s.currentYearDays}</div>
            <div className="text-xs text-slate-500">Tage · {s.currentYearEpisodes} Episoden</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">{data.prevYear} <span className={`ml-1 ${trendCls}`}>{trendIcon}</span></div>
            <div className="text-2xl font-semibold text-slate-400 tabular-nums">{s.prevYearDays}</div>
            <div className="text-xs text-slate-500">Tage · {s.prevYearEpisodes} Episoden</div>
          </div>
        </div>
      </section>

      {/* Ueberstunden-Trend */}
      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs uppercase tracking-wider text-slate-500">Zeitkonto letzte 12 Monate</h4>
          <div className={`text-sm font-semibold tabular-nums ${data.overtime.totalSaldoMinutes >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
            Total: {fmtHours(data.overtime.totalSaldoMinutes)}
          </div>
        </div>
        <div className="grid grid-cols-12 gap-1 items-end h-24">
          {data.overtime.monthlySaldos.map((m) => {
            const pct = Math.max(4, Math.round((Math.abs(m.saldoMinutes) / maxAbsSaldo) * 100));
            const positive = m.saldoMinutes >= 0;
            return (
              <div key={m.ym} className="flex flex-col items-center gap-1 min-w-0" title={`${m.ym}: ${fmtHours(m.saldoMinutes)}`}>
                <div className="w-full flex justify-center">
                  <div
                    className={`w-full rounded ${positive ? 'bg-emerald-500/50' : 'bg-rose-500/50'}`}
                    style={{ height: `${pct}%`, minHeight: '4px' }}
                  />
                </div>
                <div className="text-[9px] text-slate-500 truncate">{m.ym.slice(5)}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Urlaub */}
      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Urlaub</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-slate-500">{year}</div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-semibold text-brand-300 tabular-nums">{l.current.usedDays}</span>
              <span className="text-sm text-slate-400">/ {currBudget}</span>
            </div>
            <div className="text-xs text-slate-500">
              Verbraucht · <span className={currRemaining < 0 ? 'text-rose-300' : 'text-emerald-300'}>{currRemaining.toFixed(1)} Rest</span>
              {l.current.carryoverDays != null && l.current.carryoverDays > 0 && (
                <span className="ml-2 text-slate-500">({l.current.carryoverDays} Übertrag)</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500">{data.prevYear}</div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-semibold text-slate-400 tabular-nums">{l.previous.usedDays}</span>
              <span className="text-sm text-slate-500">/ {prevBudget}</span>
            </div>
            <div className="text-xs text-slate-500">Verbraucht (Vorjahr)</div>
          </div>
        </div>
      </section>
    </div>
  );
}

function PersonDokumente({ personId }: { personId: string }) {
  const dlg = useDialogs();
  const [docs, setDocs] = useState<PersonDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get<{ success: boolean; data: PersonDocument[] }>(
      `/modules/bibliothek/akten/by-person/${personId}/documents`,
    ).then((r) => setDocs(r.data ?? []))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [personId]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // authFetch statt rohem fetch: Datei wird vor dem Senden speichergestützt
      // (iOS-26.5-Bug WebKit 319985, Jörg 2026-10-06), Anmeldung wird erneuert.
      const res = await authFetch(`/api/modules/bibliothek/akten/by-person/${personId}/documents`, {
        method: 'POST', body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? `HTTP ${res.status}`);
      }
      load();
    } catch (err) { setError((err as Error).message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const onDelete = async (docId: string) => {
    if (!(await dlg.confirm({ title: 'Dokument löschen?', message: 'Die Datei wird endgültig aus der Mitarbeiter-Akte entfernt.', confirmLabel: 'Löschen', danger: true }))) return;
    try {
      await api.delete(`/modules/bibliothek/akten/by-person/${personId}/documents/${docId}`);
      load();
    } catch (err) { await dlg.error('Löschen fehlgeschlagen', err); }
  };

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Persönliche Dokumente</h3>
        <label className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-brand-600 hover:bg-brand-500 text-primary-foreground cursor-pointer">
          <Plus className="w-4 h-4" /> {uploading ? 'Lädt hoch…' : 'Datei hochladen'}
          <input type="file" className="hidden" onChange={onUpload} disabled={uploading} />
        </label>
      </div>
      {error && <div className="px-3 py-2 mb-3 rounded-lg text-sm bg-rose-500/10 text-rose-300">Fehler: {error}</div>}
      {loading
        ? <div className="text-sm text-slate-500">Lädt…</div>
        : docs.length === 0
          ? <div className="text-sm text-slate-500">Keine Dokumente.</div>
          : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 bg-slate-800/40">
                <tr>
                  <th className="text-left  py-2 px-3">Datei</th>
                  <th className="text-left  py-2 px-3">Hinzugefügt</th>
                  <th className="text-right py-2 px-3">Größe</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="border-t border-slate-800">
                    <td className="py-1 px-3 text-slate-100">{d.title}</td>
                    <td className="py-1 px-3 text-slate-400 whitespace-nowrap">{fmtDate(d.created_at)}</td>
                    <td className="py-1 px-3 text-right text-slate-400 tabular-nums">{fmtBytes(d.size_bytes)}</td>
                    <td className="py-1 px-3 text-right whitespace-nowrap">
                      <a href={`/api/modules/bibliothek/akten-documents/${d.id}/download`}
                         className="inline-block text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 mr-1">
                        Download
                      </a>
                      <button onClick={() => onDelete(d.id)}
                              className="inline-block text-xs px-2 py-1 rounded bg-slate-800 hover:bg-rose-500/30 text-slate-200">
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      <div className="text-xs text-slate-500 mt-2">
        Dateien liegen im Bibliothek-Modul unter einer privaten Mitarbeiter-Akte.
        Nur GF/Admin + der Mitarbeiter selbst können sie sehen.
      </div>
    </div>
  );
}

// 2026-07-06 (P4.5): PersonAkteStruktur wurde in
// components/AkteStrukturViewer.tsx ausgelagert und rendert dort sowohl
// Mitarbeiter- als auch Kunden-Akten. Der alte lokale Renderer und die
// Hilfstypen sind entfallen — Aufruf am Sub-Tab „Akte" nutzt jetzt den
// gemeinsamen Viewer.
type _RemovedPersonAkteStruktur = never;
